import { showConfirmation } from '../lib/confirmation';
import { invoiceDueTermOptions as dueTermOptions } from '../utils/invoiceDueTerms';
import { formatDate as format, formatTime, normalizeSearch, formatName } from '../lib/format';
import PayrollLedger from '../components/PayrollLedger';
import Quotes from './Quotes';
import SectionCard from '../components/ui/SectionCard';
import StatCard from '../components/ui/StatCard';
import PageContent from '../components/ui/PageContent';
import PageHeader from '../components/ui/PageHeader';
import { Table, TableHead, TableRow, TableHeaderCell, TableCell } from '../components/ui/Table';
import FieldLabel from '../components/ui/FieldLabel';
import UiCheckbox from '../components/ui/Checkbox';
import UiInput from '../components/ui/Input';
import Textarea from '../components/ui/Textarea';
import SearchInput from '../components/ui/SearchInput';
import Tabs from '../components/ui/Tabs';
import Button from '../components/ui/Button';
import ActionMenu from '../components/ui/ActionMenu';
import { withDownloadProgress } from '../utils/downloadProgress';
import { useState, useEffect, useMemo, useRef } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { useSelector } from "react-redux";
import InvoiceGenerator from "../components/InvoiceGenerator";
import { postWithAuth } from "../common";
import { toast } from "react-toastify";
import useTableSort from '../components/useTableSort';
import LoadingPopup from '../components/LoadingPopup';
import { addDays, parse, isValid, isAfter, isBefore, startOfDay, endOfDay } from 'date-fns';
import ModalComponent from "../components/Modal";
import CompactSuccessModal from '../components/CompactSuccessModal';
import MyCalendar from "../components/Calendar";
import Input from "../components/Input";
import Dropdown from "../components/Dropdown";
import Section from "../components/Section";
import MyDropzoneMulti from "../components/MyDropzoneMulti";
import { CheckIcon, Building2Icon, FileTextIcon, ReceiptIcon, ArchiveIcon, CopyIcon, ResetIcon, DownloadIcon, EditIcon, EyeIcon, TrashIcon, UsersIcon } from "../components/NavIcons";
import { randomString } from "../common";
import { useSearchParams } from "react-router-dom";
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { resolveStorageFileUrl } from '../storageUpload';

const statusFilterOptions = [
    { label: 'All invoices', value: 'all' },
    { label: 'Draft', value: 'Draft' },
    { label: 'Sent', value: 'Sent' },
    { label: 'Outstanding', value: 'Outstanding' },
    { label: 'Overdue', value: 'Overdue' },
    { label: 'Paid', value: 'Paid' },
]
const statementTypeOptions = [
    { label: 'Outstanding / overdue', value: 'outstanding' },
    { label: 'Paid', value: 'paid' },
]
const workerStatusFilterOptions = [
    { label: 'All invoices', value: 'all' },
    { label: 'Paid', value: 'Paid' },
    { label: 'Draft', value: 'Draft' },
    { label: 'Outstanding', value: 'Outstanding' },
]
const dateFilterOptions = [
    { label: 'All time', value: 'all' },
    { label: 'Last 30 days', value: '30' },
    { label: 'Last 90 days', value: '90' },
    { label: 'This year', value: 'year' },
    { label: 'Custom range', value: 'custom' },
]
const quantityTypeOptions = ['Hours', 'Days', 'Weeks', 'Unit'].map(value => ({ label: value, value }))
const paymentCategoryOptions = ['Bank transfer', 'Card payment', 'Cash', 'Credit adjustment', 'Other'].map(value => ({ label: value, value }))

const resolveDueDateFromTerm = (invoiceDateValue, dueTerm) => {
    const invoiceDate = invoiceDateValue ? new Date(invoiceDateValue) : new Date()
    if (Number.isNaN(invoiceDate.getTime())) return addDays(new Date(), 7)
    if (dueTerm === 'receipt') return invoiceDate
    if (dueTerm === 'days14') return addDays(invoiceDate, 14)
    if (dueTerm === 'days30') return addDays(invoiceDate, 30)
    if (dueTerm === 'endOfMonth30') {
        const endOfMonth = new Date(invoiceDate.getFullYear(), invoiceDate.getMonth() + 1, 0)
        return addDays(endOfMonth, 30)
    }
    return addDays(invoiceDate, 7)
}

const getDueDaysForTerm = (dueTerm) => {
    if (dueTerm === 'days14') return '14'
    if (dueTerm === 'days30') return '30'
    if (dueTerm === 'receipt' || dueTerm === 'endOfMonth30') return '0'
    return '7'
}

const formatCurrency = (value) => `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const getInvoicePaidAmount = (invoice = {}) => {
    if (Array.isArray(invoice.payments) && invoice.payments.length) {
        return invoice.payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0)
    }
    return invoice.status === 'Paid' ? Number(invoice.total || 0) : Number(invoice.amountPaid || 0)
}
const getInvoiceBalanceDue = (invoice = {}) => Math.max(0, Number(invoice.total || 0) - getInvoicePaidAmount(invoice))

const InvoicePaymentHistory = ({ invoice }) => {
    const payments = Array.isArray(invoice?.payments) ? [...invoice.payments].reverse() : []
    if (!payments.length) return null
    return <div className='invoiceHistoryBlock invoiceEditDividerSection'>
        <h2 className='invoiceEditSectionHeading'>Payment History</h2>
        <div className='invoicePaymentHistorySummary'>
            <span>Paid <strong>{formatCurrency(getInvoicePaidAmount(invoice))}</strong></span>
            <span>Balance <strong>{formatCurrency(getInvoiceBalanceDue(invoice))}</strong></span>
        </div>
        <div className='invoicePaymentHistoryScroll'>
            <Table emptyItems="payments" className='invoiceHistoryTable invoicePaymentHistoryTable'>
                <TableHead><TableRow><TableHeaderCell>Date</TableHeaderCell><TableHeaderCell>Category</TableHeaderCell><TableHeaderCell>Description</TableHeaderCell><TableHeaderCell>Includes GST</TableHeaderCell><TableHeaderCell>Amount</TableHeaderCell><TableHeaderCell>Recorded By</TableHeaderCell><TableHeaderCell>Receipt</TableHeaderCell></TableRow></TableHead>
                <tbody>{payments.map((payment, index) => {
                    const paymentDate = payment.date ? new Date(payment.date) : null
                    return <TableRow key={payment.id || `payment-${index}`}>
                        <TableCell>{paymentDate && !Number.isNaN(paymentDate.getTime()) ? format(paymentDate, 'd MMM yyyy') : '-'}</TableCell>
                        <TableCell>{payment.category || '-'}</TableCell>
                        <TableCell>{payment.description || '-'}</TableCell>
                        <TableCell>{formatCurrency(payment.gstAmount)}</TableCell>
                        <TableCell><strong>{formatCurrency(payment.amount)}</strong></TableCell>
                        <TableCell>{formatName(payment.recordedByName || '-')}</TableCell>
                        <TableCell>{payment.receiptSent ? 'Sent' : (payment.receiptRequested ? 'Not sent' : '-')}</TableCell>
                    </TableRow>
                })}</tbody>
            </Table>
        </div>
    </div>
}

const splitEmailAddresses = (value) => String(value || '').split(/[;,]/).map(email => email.trim()).filter(Boolean)
const isValidEmailAddress = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())

const formatInvoiceQuantity = (value, quantityType = 'Hours') => {
    const displayType = Number(value) === 1 && quantityType === 'Days' ? 'Day' : quantityType
    if (typeof value === 'number' && Number.isFinite(value)) {
        return `${value} ${displayType}`
    }
    const raw = String(value || '').trim()
    return raw ? `${raw} ${displayType}` : '-'
}

const getInvoiceQuantityType = (row) => (
    row?.quantityType || (/^Equipment Hire\b/i.test(String(row?.description || '').trim()) ? 'Days' : 'Hours')
)

const isWorkerInvoiceEditable = (invoice) => !!invoice?.worker && ['Created', 'Modified'].includes(invoice.status || 'Created')
const isClientInvoiceEditable = (invoice) => !!invoice?.client && !['Paid', 'Voided'].includes(invoice.status || 'Draft')
const isInvoiceEditable = (invoice) => isWorkerInvoiceEditable(invoice) || isClientInvoiceEditable(invoice)

const pdfAttachmentToObjectUrl = (attachment) => {
    const content = String(attachment?.content || '').trim()
    if (!content) return ''
    const byteCharacters = atob(content)
    const bytes = new Uint8Array(byteCharacters.length)
    for (let index = 0; index < byteCharacters.length; index += 1) {
        bytes[index] = byteCharacters.charCodeAt(index)
    }
    return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
}

const parseInvoiceLineDateValue = (value) => {
    if (!value) return null
    if (value instanceof Date) {
        return isValid(value) ? value : null
    }

    const directDate = new Date(value)
    if (isValid(directDate)) {
        return directDate
    }

    let raw = String(value || '').trim()
    raw = raw.replace(/^\(/, '').replace(/\)$/, '')
    if (raw.includes(' - ')) {
        raw = raw.split(' - ')[0].trim()
    }

    const formats = [
        'dd/MMM/yyyy h:mm a',
        'dd/MMM/yyyy',
        'dd/MM/yyyy h:mm a',
        'dd/MM/yyyy',
        'EEE, d MMM yyyy',
    ]

    for (const fmt of formats) {
        const parsed = parse(raw, fmt, new Date())
        if (isValid(parsed)) {
            return parsed
        }
    }

    return null
}

const getInvoiceLineDateTime = (value) => {
    const raw = String(value || '').trim().replace(/^\(/, '').replace(/\)$/, '')
    const [startRaw, endRaw] = raw.split(' - ').map(part => part?.trim())
    const start = parseInvoiceLineDateValue(startRaw)
    if (!start) return { date: raw || '-', time: '' }

    let time = ''
    if (/\d{1,2}:\d{2}\s*[AP]M/i.test(startRaw || '')) {
        const end = endRaw ? parse(endRaw, 'h:mm a', start) : null
        time = `${formatTime(start)}${end && isValid(end) ? ` - ${formatTime(end)}` : ''}`
    }

    return {
        date: format(start, 'EEE, d MMM yyyy'),
        time,
    }
}

const updateInvoiceLineDate = (originalValue, nextDate) => {
    const formattedDate = format(nextDate, 'dd/MMM/yyyy')
    const raw = String(originalValue || '').trim().replace(/^\(/, '').replace(/\)$/, '')
    const timeRange = raw.match(/\d{1,2}:\d{2}\s*[AP]M(?:\s*-\s*\d{1,2}:\d{2}\s*[AP]M)?/i)?.[0]
    return timeRange ? `${formattedDate} ${timeRange}` : formattedDate
}

const getInvoiceLineDescription = (row) => {
    const rawDescription = String(row.description || '').trim()
    const [rolePart, ...embeddedNames] = rawDescription.split(' - ')
    const roleMatch = rolePart.match(/^(.*?)\s+x\s*(\d+)$/i)
    const role = roleMatch ? `${roleMatch[1].trim()} x ${roleMatch[2]}` : (rolePart || '-')
    const names = Array.isArray(row.workerNames) && row.workerNames.length
        ? row.workerNames
        : embeddedNames.join(' - ').split(',').map(name => name.trim()).filter(Boolean)

    return { role, names }
}

const getClientInvoiceDescription = (invoice) => {
    if (invoice.site?.name) {
        return `Site: ${invoice.site.name}${invoice.site.address ? `, ${invoice.site.address}` : ''}`
    }
    if (invoice.jobs?.length) return invoice.jobs[0].description
    if (invoice.manualLineItems?.length) return invoice.manualLineItems[0].description
    if (invoice.additionalJobs?.length) return invoice.additionalJobs[0].description
    return '-'
}

const getInvoiceStatusMeta = (invoice) => {
    const status = invoice.status || 'Draft'
    if (invoice.deliveryChangesPending && !['Draft', 'Paid', 'Voided'].includes(status)) {
        return { label: 'Updated - resend required', tone: 'outstanding' }
    }
    if (status === 'Overdue') {
        const dueDate = invoice.dueDate ? new Date(invoice.dueDate) : null
        if (dueDate && !Number.isNaN(dueDate.getTime())) {
            const diffMs = new Date().setHours(0, 0, 0, 0) - new Date(dueDate).setHours(0, 0, 0, 0)
            const overdueDays = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)))
            return { label: `${overdueDays} day${overdueDays === 1 ? '' : 's'} overdue`, tone: 'overdue' }
        }
    }
    if (status === 'Paid') return { label: 'Paid', tone: 'paid' }
    if (status === 'Voided') return { label: 'Voided', tone: 'voided' }
    if (status === 'Outstanding') return { label: 'Outstanding', tone: 'outstanding' }
    if (status === 'Sent') return { label: 'Sent', tone: 'sent' }
    return { label: 'Draft', tone: 'draft' }
}

const Invoices = () => {
    const [successMessage, setSuccessMessage] = useState(null)

    const [loading, setLoading] = useState(false)
    const [invoices, setInvoices] = useState([])
    const [chosenInvoice, setChosenInvoice] = useState('')
    const [reviewInvoice, setReviewInvoice] = useState(null)
    const [reviewTimesheets, setReviewTimesheets] = useState([])
    const [reviewTimesheetIndex, setReviewTimesheetIndex] = useState(0)
    const [reviewTimesheetsLoading, setReviewTimesheetsLoading] = useState(false)
    const [reviewTimesheetsError, setReviewTimesheetsError] = useState('')
    const reviewRequestRef = useRef(0)
    const reviewObjectUrlsRef = useRef([])
    const emailAttachmentObjectUrlsRef = useRef([])
    const closeInvoiceReviewRef = useRef(() => { })
    const changeReviewTimesheetRef = useRef(() => { })
    const [searchParams] = useSearchParams()
    const openDraftEditorFromUrl = searchParams.get('editDraft') === '1'

    const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') === 'quotes' ? 'Quotes' : 'Clients')

    const [clients, setClients] = useState([])
    const [workers, setWorkers] = useState([])
    const [sites, setSites] = useState([])

    const userInfo = useSelector((state) => state.user.user)
    const activeOrg = useSelector((state) => state.user.activeOrg)
    const activeOrgInfo = useSelector((state) => state.user.activeOrgInfo)

    const [shouldUploadAndSendInvoice, setShouldUploadAndSendInvoice] = useState(false)
    const [invoiceEmailDraft, setInvoiceEmailDraft] = useState(null)
    const [uploading, setUploading] = useState(false)
    const [loadingMessage, setLoadingMessage] = useState('')
    const [invoiceGenerationMessage, setInvoiceGenerationMessage] = useState('')

    const [clientDueDate, setClientDueDate] = useState('')
    const [clientDueDays, setClientDueDays] = useState('7')
    const [invoiceDate, setInvoiceDate] = useState('')
    const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('')
    const [additionalJobsModifyingInvoice, setAdditionalJobsModifyingInvoice] = useState()
    const [showAdditionalJobs, setShowAdditionalJobs] = useState(false)
    const [invoiceJobs, setInvoiceJobs] = useState([])
    const [manualLineItems, setManualLineItems] = useState([])
    const [clientDueTerm, setClientDueTerm] = useState('days7')
    const [invoiceAttachments, setInvoiceAttachments] = useState([])
    const [timesheetAttachments, setTimesheetAttachments] = useState([])
    const [autoReminderEnabled, setAutoReminderEnabled] = useState(true)
    const [selectedStatusFilter, setSelectedStatusFilter] = useState('all')
    const [selectedDateFilter, setSelectedDateFilter] = useState('all')
    const [searchTerm, setSearchTerm] = useState('')
    const [workerSearchTerm, setWorkerSearchTerm] = useState('')
    const [workerStatusFilter, setWorkerStatusFilter] = useState('all')
    const [workerDateFilter, setWorkerDateFilter] = useState('all')
    const [workerCustomDateFrom, setWorkerCustomDateFrom] = useState('')
    const [workerCustomDateTo, setWorkerCustomDateTo] = useState('')
    const [clientInvoicePage, setClientInvoicePage] = useState(1)
    // Keep edited invoices in their existing position while this list is open.
    // Issue dates remain the source of truth for display and date filters.
    const [clientInvoiceSortDates, setClientInvoiceSortDates] = useState({})
    const [workerInvoicePage, setWorkerInvoicePage] = useState(1)
    const invoicePageSize = 20
    const [customDateFrom, setCustomDateFrom] = useState('')
    const [customDateTo, setCustomDateTo] = useState('')
    const [showMarkPaidModal, setShowMarkPaidModal] = useState(false)
    const [invoiceMarkingPaid, setInvoiceMarkingPaid] = useState(null)
    const clientNameById = useMemo(
        () => new Map(clients.map(client => [client.id, client.name || ''])),
        [clients]
    )
    const workerNameById = useMemo(
        () => new Map(workers.map(worker => [worker.id, formatName(`${worker.firstName || ''} ${worker.lastName || ''}`).trim()])),
        [workers]
    )
    const [paidDateValue, setPaidDateValue] = useState(new Date())
    const [paymentAmount, setPaymentAmount] = useState('')
    const [paymentCategory, setPaymentCategory] = useState('Bank transfer')
    const [paymentDescription, setPaymentDescription] = useState('')
    const [sendPaymentReceipt, setSendPaymentReceipt] = useState(false)

    const [showEditAdditionalJob, setShowEditAdditionalJob] = useState(false)
    const [showAdditionalJobIndex, setShowAdditionalJobIndex] = useState(-1)
    const [showEditingPrimaryJob, setShowEditingPrimaryJob] = useState(false)
    const [lineItemPendingDelete, setLineItemPendingDelete] = useState(null)
    const [manualInvoiceStartDate, setManualInvoiceStartDate] = useState('')
    const [manualInvoiceEndDate, setManualInvoiceEndDate] = useState('')
    const [manualInvoiceClientId, setManualInvoiceClientId] = useState('')
    const [manualInvoiceSiteId, setManualInvoiceSiteId] = useState('')
    const [manualWorkerInvoiceStartDate, setManualWorkerInvoiceStartDate] = useState('')
    const [manualWorkerInvoiceEndDate, setManualWorkerInvoiceEndDate] = useState('')
    const [manualInvoiceWorkerId, setManualInvoiceWorkerId] = useState('')
    const [invoiceGenerationPreview, setInvoiceGenerationPreview] = useState(null)
    const [clientInvoiceView, setClientInvoiceView] = useState('active')
    const [invoicePendingVoid, setInvoicePendingVoid] = useState(null)
    const [invoicePendingSent, setInvoicePendingSent] = useState(null)
    const [markingSent, setMarkingSent] = useState(false)
    const [invoicePendingDuplicate, setInvoicePendingDuplicate] = useState(null)
    const [showInvoiceFavourites, setShowInvoiceFavourites] = useState(false)
    const [invoicePreview, setInvoicePreview] = useState(null)
    const [invoicePreviewUrl, setInvoicePreviewUrl] = useState('')
    const [invoicePreviewFailed, setInvoicePreviewFailed] = useState(false)
    const [voidReason, setVoidReason] = useState('')
    const [statementClientId, setStatementClientId] = useState('')
    const [statementType, setStatementType] = useState('outstanding')
    const [statementBusy, setStatementBusy] = useState(false)
    const [discountAmount, setDiscountAmount] = useState('')
    const [discountDescription, setDiscountDescription] = useState('Discount')
    const [discountGstEnabled, setDiscountGstEnabled] = useState(true)
    const invoiceEditTotals = useMemo(() => {
        const rows = [...(invoiceJobs || []), ...(manualLineItems || [])]
        const subtotal = rows.reduce((sum, row) => {
            const storedSubtotal = Number(row.subtotal)
            const resolvedSubtotal = Number.isFinite(storedSubtotal) ? storedSubtotal : Number(row.rate || 0) * Number(row.quantity || 0)
            return sum + resolvedSubtotal
        }, 0)
        const grossGst = rows.reduce((sum, row) => sum + Number(row.gst ?? 0), 0)
        const safeDiscount = Math.min(Math.max(0, Number(discountAmount) || 0), Math.max(0, subtotal))
        const discountGst = discountGstEnabled ? Math.min(grossGst, safeDiscount * 0.1) : 0
        const discountedSubtotal = subtotal - safeDiscount
        const gst = grossGst - discountGst
        return { subtotal: discountedSubtotal, gst, total: discountedSubtotal + gst, discountAmount: safeDiscount, discountGst }
    }, [invoiceJobs, manualLineItems, discountAmount, discountGstEnabled])
    const invoiceFavouriteRows = useMemo(() => {
        const unique = new Map()
        invoices.forEach(invoice => {
            const rows = [...(invoice.manualLineItems || []), ...(invoice.additionalJobs || [])]
            rows.forEach(row => {
                const description = String(row?.description || '').trim()
                if (!description) return
                const key = `${description.toLowerCase()}|${Number(row.rate || 0)}|${row.quantityType || 'Hours'}|${!!row.textOnly}`
                if (!unique.has(key)) unique.set(key, { ...row, description })
            })
        })
        return Array.from(unique.values()).sort((first, second) => first.description.localeCompare(second.description))
    }, [invoices])
    const [additionalJobDescription, setAdditionalJobDescription] = useState('')
    const [additionalJobQuantity, setAdditionalJobQuantity] = useState('')
    const [additionalJobQuantityType, setAdditionalJobQuantityType] = useState('Hours')
    const [additionalJobGstEnabled, setAdditionalJobGstEnabled] = useState(true)
    const [additionalJobRate, setAdditionalJobRate] = useState('')
    const [additionalJobRateFocused, setAdditionalJobRateFocused] = useState(false)
    const [additionalJobTime, setAdditionalJobTime] = useState('')
    const [additionalJobTextOnly, setAdditionalJobTextOnly] = useState(false)


    const isOrgAdmin = activeOrg?.role === 'admin'

    useEffect(() => {
        if (activeOrg && isOrgAdmin) {
            fetchInvoices()
            fetchClients()
            fetchWorkers()
            fetchSites()
        }
    }, [activeOrg, isOrgAdmin])


    const fetchWorkers = async () => {

        try {

            const resp = await postWithAuth('fetchWorkers', { orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setWorkers(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }


    const fetchClients = async () => {

        try {

            const resp = await postWithAuth('fetchClients', { orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setClients(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchSites = async () => {
        try {
            const resp = await postWithAuth('fetchSites', { orgId: activeOrg.id })
            if (resp?.err) {
                toast.error(resp.err)
                return
            }
            setSites(Array.isArray(resp?.data) ? resp.data : [])
        } catch (e) {
            toast.error(e.message)
        }
    }


    useEffect(() => {
        setChosenInvoice('')
        setInvoiceGenerationPreview(null)
        setInvoiceGenerationMessage('')
    }, [activeTab])

    useEffect(() => {
        if (!openDraftEditorFromUrl || invoices.length === 0 || additionalJobsModifyingInvoice) return
        const draftInvoice = invoices.find(invoice => invoice.status === 'Draft')
        if (!draftInvoice) return
        openInvoiceEditor(draftInvoice)
    }, [openDraftEditorFromUrl, invoices, additionalJobsModifyingInvoice])

    useEffect(() => {
        if (!additionalJobsModifyingInvoice) return
        setInvoiceJobs(additionalJobsModifyingInvoice.jobs || [])
        const storedDueDays = Number(additionalJobsModifyingInvoice.dueDays)
        const resolvedDueDays = additionalJobsModifyingInvoice.dueDays !== undefined && additionalJobsModifyingInvoice.dueDays !== null && additionalJobsModifyingInvoice.dueDays !== '' && Number.isFinite(storedDueDays)
            ? String(storedDueDays)
            : getDueDaysForTerm(additionalJobsModifyingInvoice.dueTerm || 'days7')
        setClientDueDays(resolvedDueDays)
        setClientDueDate(additionalJobsModifyingInvoice.dueDate ? new Date(additionalJobsModifyingInvoice.dueDate) : resolveDueDateFromTerm(additionalJobsModifyingInvoice.date, additionalJobsModifyingInvoice.dueTerm || 'days7'))
        setInvoiceDate(additionalJobsModifyingInvoice.date ? new Date(additionalJobsModifyingInvoice.date) : new Date())
        setClientDueTerm(additionalJobsModifyingInvoice.dueTerm || 'days7')
        setPurchaseOrderNumber(additionalJobsModifyingInvoice.poNumber || '')
        // Save stores every row as a manual line item and clears additionalJobs, so rows a
        // worker added since (additionalJobs) are merged in rather than dropped. Older
        // invoices may hold the same row in both lists; those appear once.
        const storedManualRows = Array.isArray(additionalJobsModifyingInvoice.manualLineItems) ? additionalJobsModifyingInvoice.manualLineItems : []
        const rowKey = row => row?.id ? `id:${row.id}` : `row:${row?.description || ''}|${row?.quantity ?? ''}|${row?.rate ?? ''}|${row?.total ?? ''}`
        const storedKeys = new Set(storedManualRows.map(rowKey))
        setManualLineItems([
            ...storedManualRows,
            ...(Array.isArray(additionalJobsModifyingInvoice.additionalJobs) ? additionalJobsModifyingInvoice.additionalJobs : []).filter(row => !storedKeys.has(rowKey(row))),
        ])
        setInvoiceAttachments(additionalJobsModifyingInvoice.attachments || [])
        setTimesheetAttachments(additionalJobsModifyingInvoice.timesheetAttachments || [])
        setAutoReminderEnabled(additionalJobsModifyingInvoice.autoReminderEnabled !== false)
        setDiscountAmount(additionalJobsModifyingInvoice.discountAmount ? String(additionalJobsModifyingInvoice.discountAmount) : '')
        setDiscountDescription(additionalJobsModifyingInvoice.discountDescription || 'Discount')
        setDiscountGstEnabled(additionalJobsModifyingInvoice.discountGstEnabled !== false)
    }, [additionalJobsModifyingInvoice])

    // Due date/days are only recomputed when the user changes the term or invoice date,
    // so stored values (including custom due days such as 21 or 45) survive opening and saving.
    const handleClientDueTermChange = (nextTerm) => {
        const resolvedTerm = nextTerm || 'days7'
        setClientDueTerm(resolvedTerm)
        setClientDueDays(getDueDaysForTerm(resolvedTerm))
        setClientDueDate(resolveDueDateFromTerm(invoiceDate || additionalJobsModifyingInvoice?.date, resolvedTerm))
    }

    const handleInvoiceDateChange = (nextDate) => {
        setInvoiceDate(nextDate)
        if (!nextDate) return
        const numericDays = Number(clientDueDays)
        if (clientDueTerm === 'receipt' || clientDueTerm === 'endOfMonth30' || !Number.isFinite(numericDays) || numericDays <= 0) {
            setClientDueDate(resolveDueDateFromTerm(nextDate, clientDueTerm))
            return
        }
        setClientDueDate(addDays(new Date(nextDate), numericDays))
    }

    const openInvoiceEditor = (invoice) => {
        // A fresh object forces the load effect to re-initialise from the stored invoice,
        // discarding any cancelled edits from a previous open of the same invoice.
        setAdditionalJobsModifyingInvoice({ ...invoice })
        setShowAdditionalJobs(true)
    }

    const fetchInvoices = async () => {

        try {

            const resp = await postWithAuth('fetchInvoices', { orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setInvoices(resp.data)
                return resp.data
            }
        } catch (e) {
            toast.error(e.message)
        }
        return []
    }

    const visibleInvoices = useMemo(
        () => invoices
            .filter(row => activeTab === 'Clients' ? row.client : !row.client)
            .map(row => ({
                ...row,
                clientName: row.client?.name || clientNameById.get(row.client?.id) || '',
                siteName: row.site?.name || '',
                workerName: row.worker?.name || workerNameById.get(row.worker?.id) || '',
            })),
        [invoices, activeTab, clientNameById, workerNameById]
    )
    const invoicesSort = useTableSort(visibleInvoices, { key: 'date', direction: 'descending' });
    const selectedStatusFilterOption = useMemo(
        () => statusFilterOptions.find(option => option.value === selectedStatusFilter) || statusFilterOptions[0],
        [selectedStatusFilter]
    )
    const selectedDateFilterOption = useMemo(
        () => dateFilterOptions.find(option => option.value === selectedDateFilter) || dateFilterOptions[0],
        [selectedDateFilter]
    )
    const selectedWorkerDateFilterOption = useMemo(
        () => dateFilterOptions.find(option => option.value === workerDateFilter) || dateFilterOptions[0],
        [workerDateFilter]
    )
    const selectedWorkerStatusFilterOption = useMemo(
        () => workerStatusFilterOptions.find(option => option.value === workerStatusFilter) || workerStatusFilterOptions[0],
        [workerStatusFilter]
    )
    const manualInvoiceClientOptions = useMemo(
        () => [{ label: 'All clients', value: '' }, ...clients.map(client => ({ label: client.name || 'Client', value: client.id }))],
        [clients]
    )
    const manualInvoiceSiteOptions = useMemo(() => {
        const availableSites = manualInvoiceClientId
            ? sites.filter(site => site.clientId === manualInvoiceClientId && !site.archived)
            : []
        return [{ label: manualInvoiceClientId ? 'All sites' : 'Select a client first', value: '' }, ...availableSites.map(site => ({
            label: site.name || site.address || 'Site',
            value: site.id,
        }))]
    }, [sites, manualInvoiceClientId])
    const manualInvoiceWorkerOptions = useMemo(
        () => [{ label: 'All workers', value: '' }, ...workers
            .filter(worker => worker.userType !== 'Employee' && !worker.archived)
            .map(worker => ({ label: formatName(`${worker.firstName || ''} ${worker.lastName || ''}`.trim() || 'Worker'), value: worker.id }))],
        [workers]
    )
    const statementClientOptions = useMemo(
        () => [{ label: 'Select client', value: '' }, ...clients.map(client => ({ label: client.name || 'Client', value: client.id }))],
        [clients]
    )

    const filteredClientInvoices = useMemo(() => {
        const now = new Date()
        return invoicesSort.sortedData.filter(invoice => {
            const isVoided = (invoice.status || '') === 'Voided'
            if (clientInvoiceView === 'voided' ? !isVoided : isVoided) return false
            const matchesStatus = selectedStatusFilter === 'all' || (invoice.status || 'Draft') === selectedStatusFilter
            const invoiceDate = invoice.date ? new Date(invoice.date) : null
            let matchesDate = true
            if (selectedDateFilter === '30' && invoiceDate && !Number.isNaN(invoiceDate.getTime())) {
                matchesDate = (now - invoiceDate) <= 30 * 24 * 60 * 60 * 1000
            } else if (selectedDateFilter === '90' && invoiceDate && !Number.isNaN(invoiceDate.getTime())) {
                matchesDate = (now - invoiceDate) <= 90 * 24 * 60 * 60 * 1000
            } else if (selectedDateFilter === 'year' && invoiceDate && !Number.isNaN(invoiceDate.getTime())) {
                matchesDate = invoiceDate.getFullYear() === now.getFullYear()
            } else if (selectedDateFilter === 'custom') {
                const fromValid = customDateFrom instanceof Date && !Number.isNaN(customDateFrom.getTime())
                const toValid = customDateTo instanceof Date && !Number.isNaN(customDateTo.getTime())
                if (fromValid && invoiceDate) {
                    matchesDate = !isBefore(invoiceDate, startOfDay(customDateFrom))
                }
                if (matchesDate && toValid && invoiceDate) {
                    matchesDate = !isAfter(invoiceDate, endOfDay(customDateTo))
                }
            }

            const haystack = [
                invoice.invoiceNumber,
                invoice.clientName,
                invoice.siteName,
                getClientInvoiceDescription(invoice),
            ].join(' ').toLowerCase()
            const matchesSearch = !searchTerm.trim() || haystack.includes(normalizeSearch(searchTerm.trim()))

            return matchesStatus && matchesDate && matchesSearch
        }).sort((first, second) => {
            if (clientInvoiceView === 'active') {
                const statusOrder = { Draft: 0, Overdue: 1, Outstanding: 2 }
                const statusDifference = (statusOrder[first.status || 'Draft'] ?? 3) - (statusOrder[second.status || 'Draft'] ?? 3)
                if (statusDifference) return statusDifference
            }
            return new Date(clientInvoiceSortDates[second.id] ?? second.date ?? 0).getTime() - new Date(clientInvoiceSortDates[first.id] ?? first.date ?? 0).getTime()
        })
    }, [invoicesSort.sortedData, selectedStatusFilter, selectedDateFilter, searchTerm, customDateFrom, customDateTo, clientInvoiceView, clientInvoiceSortDates])

    const clientInvoiceSummary = useMemo(() => {
        const summaryInvoices = clientInvoiceView === 'voided'
            ? invoicesSort.sortedData.filter(invoice => (invoice.status || '') !== 'Voided')
            : filteredClientInvoices
        return summaryInvoices.reduce((acc, invoice) => {
            const total = Number(invoice.total || 0)
            const paidAmount = getInvoicePaidAmount(invoice)
            const balanceDue = getInvoiceBalanceDue(invoice)
            if (invoice.status === 'Overdue') {
                acc.overdueAmount += balanceDue
                acc.overdueCount += 1
            }
            acc.paidAmount += paidAmount
            if ((invoice.status || 'Draft') === 'Draft') {
                acc.draftAmount += total
            }
            if (invoice.status === 'Outstanding' || invoice.status === 'Sent') {
                acc.outstandingAmount += balanceDue
            }
            return acc
        }, {
            overdueAmount: 0,
            overdueCount: 0,
            paidAmount: 0,
            outstandingAmount: 0,
            draftAmount: 0,
        })
    }, [filteredClientInvoices, clientInvoiceView, invoicesSort.sortedData])

    const filteredWorkerInvoices = useMemo(() => {
        const query = normalizeSearch(workerSearchTerm.trim())
        const now = new Date()
        return invoicesSort.sortedData.filter(invoice => {
            const matchesWorker = !query || normalizeSearch(String(invoice.workerName || '')).includes(query)
            const normalizedWorkerStatus = ['Created', 'Modified'].includes(invoice.status) ? 'Draft' : (invoice.status || 'Draft')
            const matchesStatus = workerStatusFilter === 'all' || normalizedWorkerStatus === workerStatusFilter
            const invoiceDate = invoice.date ? new Date(invoice.date) : null
            let matchesDate = true
            if (workerDateFilter === '30' && invoiceDate && !Number.isNaN(invoiceDate.getTime())) {
                matchesDate = (now - invoiceDate) <= 30 * 24 * 60 * 60 * 1000
            } else if (workerDateFilter === '90' && invoiceDate && !Number.isNaN(invoiceDate.getTime())) {
                matchesDate = (now - invoiceDate) <= 90 * 24 * 60 * 60 * 1000
            } else if (workerDateFilter === 'year' && invoiceDate && !Number.isNaN(invoiceDate.getTime())) {
                matchesDate = invoiceDate.getFullYear() === now.getFullYear()
            } else if (workerDateFilter === 'custom' && invoiceDate && !Number.isNaN(invoiceDate.getTime())) {
                if (workerCustomDateFrom) matchesDate = !isBefore(invoiceDate, startOfDay(new Date(workerCustomDateFrom)))
                if (matchesDate && workerCustomDateTo) matchesDate = !isAfter(invoiceDate, endOfDay(new Date(workerCustomDateTo)))
            }
            return matchesWorker && matchesStatus && matchesDate
        }).sort((first, second) => new Date(second.date || 0).getTime() - new Date(first.date || 0).getTime())
    }, [invoicesSort.sortedData, workerSearchTerm, workerStatusFilter, workerDateFilter, workerCustomDateFrom, workerCustomDateTo])

    const clientInvoicePageCount = Math.max(1, Math.ceil(filteredClientInvoices.length / invoicePageSize))
    const workerInvoicePageCount = Math.max(1, Math.ceil(filteredWorkerInvoices.length / invoicePageSize))
    const paginatedClientInvoices = useMemo(
        () => filteredClientInvoices.slice((clientInvoicePage - 1) * invoicePageSize, clientInvoicePage * invoicePageSize),
        [filteredClientInvoices, clientInvoicePage]
    )
    const paginatedWorkerInvoices = useMemo(
        () => filteredWorkerInvoices.slice((workerInvoicePage - 1) * invoicePageSize, workerInvoicePage * invoicePageSize),
        [filteredWorkerInvoices, workerInvoicePage]
    )

    useEffect(() => {
        setClientInvoicePage(current => Math.min(current, clientInvoicePageCount))
    }, [clientInvoicePageCount])

    useEffect(() => {
        setWorkerInvoicePage(current => Math.min(current, workerInvoicePageCount))
    }, [workerInvoicePageCount])

    useEffect(() => {
        setClientInvoicePage(1)
    }, [selectedStatusFilter, selectedDateFilter, searchTerm, customDateFrom, customDateTo, clientInvoiceView])

    useEffect(() => {
        setWorkerInvoicePage(1)
    }, [workerSearchTerm, workerStatusFilter, workerDateFilter, workerCustomDateFrom, workerCustomDateTo])

    const workerInvoiceSummary = useMemo(() => {
        return filteredWorkerInvoices.reduce((acc, invoice) => {
            const total = Number(invoice.total || 0)
            if (invoice.status === 'Overdue') {
                acc.overdueAmount += total
                acc.overdueCount += 1
            }
            if (invoice.status === 'Paid') acc.paidAmount += total
            if (invoice.status === 'Outstanding' || invoice.status === 'Sent') acc.outstandingAmount += total
            return acc
        }, {
            outstandingAmount: 0,
            overdueAmount: 0,
            overdueCount: 0,
            paidAmount: 0,
        })
    }, [filteredWorkerInvoices])


    // const handleSaveTimeSheet = async () => {


    //     try {
    //         setLoading(true)

    //         const payload = {
    //             id: editId,
    //             clockIn: clockIn.toISOString(),
    //             clockOut: clockOut.toISOString(),
    //             orgId: activeOrg.id,
    //         }

    //         const resp = await postWithAuth('saveTimeSheet', payload)

    //         setIsAdding(false)

    //         setSuccessMessage('TimeSheet information saved')
    //         setLoading(false)
    //         fetchTimeSheet()

    //     } catch (e) {
    //         toast.error(e.message)
    //         setLoading(false)
    //     }
    // }


    // const handleView = (timeSheet) => {

    //     setClockIn(timeSheet.clockIn ? new Date(timeSheet.clockIn) : '')
    //     setClockOut(timeSheet.clockOut ? new Date(timeSheet.clockOut) : '')
    //     setIsAdding(true)
    //     setEditId(timeSheet.id)
    // }

    // const clearFields = () => {
    //     setClockIn('')
    //     setClockOut('')
    //     setEditId('')
    // }

    const companyPayload = useMemo(() => activeOrgInfo ? {
        name: activeOrgInfo.name,
        address: activeOrgInfo.address,
        phone: activeOrgInfo.phone,
        email: activeOrgInfo.primaryEmail,
        abn: activeOrgInfo.abn,

        accountName: activeOrgInfo.accountName,
        bsb: activeOrgInfo.bsb,
        accountNumber: activeOrgInfo.accountNumber,

    } : {}, [activeOrgInfo])

    const previewInvoicePayload = useMemo(() => invoicePreview ? {
        ...invoicePreview,
        company: companyPayload,
    } : null, [invoicePreview, companyPayload])

    const memoInvoice = useMemo(() => {

        if (chosenInvoice) {
            if (chosenInvoice.client && chosenInvoice.client.id) {
                // For company
                const matchingClient = clients.find(r => r.id === chosenInvoice.client.id)
                if (!matchingClient) {
                    return {
                        ...chosenInvoice,
                        company: companyPayload,
                    }
                }

                return {
                    ...chosenInvoice,
                    company: companyPayload,
                    client: {
                        name: matchingClient.name,
                        address: matchingClient.address,
                        phone: matchingClient.contactNo,
                        email: matchingClient.invoiceEmail,
                        abn: matchingClient.abn,
                        invoiceCcEmails: matchingClient.invoiceCcEmails || '',
                        invoiceBccEmails: matchingClient.invoiceBccEmails || '',
                    },
                }
            } else {
                // For worker
                const matchingWorker = workers.find(r => r.id === chosenInvoice.worker.id)

                if (!matchingWorker) {
                    toast.error('Worker no longer exists- using old worker data')

                    return {
                        ...chosenInvoice,
                        company: companyPayload,
                    }
                }

                return {
                    ...chosenInvoice,
                    company: companyPayload,
                    worker: {
                        id: matchingWorker.id,
                        name: matchingWorker.firstName + ' ' + matchingWorker.lastName,
                        address: matchingWorker.address,
                        phone: matchingWorker.contactNo || matchingWorker.phone,
                        email: matchingWorker.email,
                        abn: matchingWorker.abn,

                        accountName: matchingWorker.accountName,
                        bsb: matchingWorker.bsb,
                        accountNumber: matchingWorker.accountNumber,
                    },

                }
            }
        }
    }, [chosenInvoice, clients, workers, companyPayload]);

    const releaseReviewObjectUrls = () => {
        reviewObjectUrlsRef.current.forEach(url => URL.revokeObjectURL(url))
        reviewObjectUrlsRef.current = []
    }

    const closeInvoiceReview = () => {
        reviewRequestRef.current += 1
        releaseReviewObjectUrls()
        setReviewInvoice(null)
        setAdditionalJobsModifyingInvoice(undefined)
        setReviewTimesheets([])
        setReviewTimesheetIndex(0)
        setReviewTimesheetsError('')
        setReviewTimesheetsLoading(false)
    }

    useEffect(() => () => {
        reviewRequestRef.current += 1
        releaseReviewObjectUrls()
        emailAttachmentObjectUrlsRef.current.forEach(url => URL.revokeObjectURL(url))
    }, [])

    const closeInvoiceEmailDraft = () => {
        emailAttachmentObjectUrlsRef.current.forEach(url => URL.revokeObjectURL(url))
        emailAttachmentObjectUrlsRef.current = []
        setInvoiceEmailDraft(null)
    }

    const openInvoiceEmailAttachment = async (attachment) => {
        let pendingWindow = null
        try {
            if (attachment?.previewInvoice) {
                if (attachment.file) {
                    pendingWindow = window.open('about:blank', '_blank')
                    if (pendingWindow) pendingWindow.opener = null
                    const resolvedUrl = await resolveStorageFileUrl(attachment.file, activeOrg.id)
                    if (resolvedUrl) {
                        if (pendingWindow) pendingWindow.location.replace(resolvedUrl)
                        else window.open(resolvedUrl, '_blank', 'noopener,noreferrer')
                        return
                    }
                    if (pendingWindow) pendingWindow.close()
                    pendingWindow = null
                }
                setShouldUploadAndSendInvoice(false)
                setChosenInvoice(attachment.invoice)
                showConfirmation('The current invoice PDF is being downloaded for preview.', { tone: 'info' })
                return
            }
            if (!attachment?.url) {
                pendingWindow = window.open('about:blank', '_blank')
                if (pendingWindow) pendingWindow.opener = null
            }
            const resolvedUrl = attachment?.url || await resolveStorageFileUrl(attachment.file, activeOrg.id)
            if (!resolvedUrl) throw new Error('File unavailable')
            if (pendingWindow) pendingWindow.location.replace(resolvedUrl)
            else window.open(resolvedUrl, '_blank', 'noopener,noreferrer')
        } catch (error) {
            if (pendingWindow) pendingWindow.close()
            toast.error(`Unable to open ${attachment?.name || 'attachment'}.`)
        }
    }

    useEffect(() => {
        if (!reviewInvoice) return undefined
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') closeInvoiceReviewRef.current()
            if (event.key === 'ArrowLeft') changeReviewTimesheetRef.current(-1)
            if (event.key === 'ArrowRight') changeReviewTimesheetRef.current(1)
        }
        document.body.classList.add('invoiceReviewOpen')
        window.addEventListener('keydown', handleKeyDown)
        return () => {
            document.body.classList.remove('invoiceReviewOpen')
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [reviewInvoice, reviewTimesheets.length])

    const openInvoiceReview = async (invoice) => {
        releaseReviewObjectUrls()
        const requestId = reviewRequestRef.current + 1
        reviewRequestRef.current = requestId
        setReviewInvoice(invoice)
        setAdditionalJobsModifyingInvoice({ ...invoice })
        setReviewTimesheets([])
        setReviewTimesheetIndex(0)
        setReviewTimesheetsError('')

        if (!invoice?.client) {
            setReviewTimesheetsLoading(false)
            return
        }

        setReviewTimesheetsLoading(true)
        try {
            const resp = await postWithAuth('fetchInvoiceAllocationPdfs', {
                id: invoice.id,
                orgId: activeOrg.id,
            })
            if (requestId !== reviewRequestRef.current) return
            if (resp?.err) throw new Error(resp.err)

            const timesheets = (Array.isArray(resp?.data) ? resp.data : []).map((attachment, index) => {
                const url = pdfAttachmentToObjectUrl(attachment)
                if (url) reviewObjectUrlsRef.current.push(url)
                return {
                    name: attachment?.name || `allocation_${index + 1}.pdf`,
                    url,
                }
            }).filter(row => row.url)
            setReviewTimesheets(timesheets)
        } catch (error) {
            if (requestId === reviewRequestRef.current) {
                setReviewTimesheetsError(error?.message || 'Unable to load timesheets.')
            }
        } finally {
            if (requestId === reviewRequestRef.current) setReviewTimesheetsLoading(false)
        }
    }

    const changeReviewTimesheet = (direction) => {
        setReviewTimesheetIndex(current => {
            const count = reviewTimesheets.length
            if (!count) return 0
            return (current + direction + count) % count
        })
    }

    closeInvoiceReviewRef.current = closeInvoiceReview
    changeReviewTimesheetRef.current = changeReviewTimesheet


    const handleSendInvoice = async (url) => {
        try {
            let payload = {
                id: chosenInvoice.id,
                url,
                invoiceNumber: chosenInvoice.invoiceNumber,
            }

            const isClientInvoice = !!chosenInvoice?.client
            if (isClientInvoice) {
                payload = {
                    ...payload,
                    type: 'client',
                    name: memoInvoice?.client?.name || chosenInvoice.client?.name || '',
                    email: chosenInvoice.recipientEmail || memoInvoice?.client?.email || chosenInvoice.client?.email || '',
                    ccEmails: Object.prototype.hasOwnProperty.call(chosenInvoice, 'ccEmails')
                        ? chosenInvoice.ccEmails
                        : (memoInvoice?.client?.invoiceCcEmails || ''),
                    bccEmails: memoInvoice?.client?.invoiceBccEmails || '',
                    attachments: chosenInvoice.attachments || [],
                    timesheetAttachments: chosenInvoice.timesheetAttachments || [],
                    emailBody: chosenInvoice.emailBody || '',
                    emailSubject: chosenInvoice.emailSubject || '',
                }
            }

            const resp = await postWithAuth(isClientInvoice ? 'sendInvoice' : 'workerSignsInvoice', {
                ...payload,
                orgId: activeOrg.id
            })

            if (resp?.err) {
                toast.error(resp.err)
                return false
            }

            if (resp?.data) {
                setSuccessMessage(isClientInvoice ? 'Invoice sent' : 'Invoice updated')
                await fetchInvoices()
                return true
            }
            toast.error('Invoice could not be sent.')
            return false
        } catch (error) {
            toast.error(error?.message || 'Invoice could not be sent.')
            return false
        } finally {
            setShouldUploadAndSendInvoice(false)
        }
    }

    const downloadClientInvoiceReport = () => withDownloadProgress(async () => {
        const headers = ['Invoice Number', 'Client', 'Site', 'Status', 'Issued Date', 'Due Date', 'Paid Date', 'Due Days', 'Total']
        const rows = filteredClientInvoices.map((invoice) => [
            invoice.invoiceNumber || '',
            invoice.clientName || invoice.client?.name || '',
            invoice.site?.name || '',
            invoice.status || 'Draft',
            invoice.date ? format(new Date(invoice.date), 'dd/MMM/yyyy') : '',
            invoice.dueDate ? format(new Date(invoice.dueDate), 'dd/MMM/yyyy') : '',
            invoice.paidDate ? format(new Date(invoice.paidDate), 'dd/MMM/yyyy') : '',
            invoice.dueDays || 7,
            Number(invoice.total || 0).toFixed(2),
        ])

        const csv = [
            headers.join(','),
            ...rows.map((row) => row.map((value) => `"${String(value || '').replace(/"/g, '""')}"`).join(',')),
        ].join('\n')

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const objectUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = objectUrl
        link.download = `client-invoice-report-${format(new Date(), 'yyyy-MM-dd')}.csv`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
    })

    const downloadAllocationPdfsForInvoice = async (invoice) => {
        if (!invoice?.id || !invoice?.client) return
        const resp = await postWithAuth('fetchInvoiceAllocationPdfs', {
            id: invoice.id,
            orgId: activeOrg.id,
        })
        if (resp?.err) {
            toast.error(resp.err)
            return
        }
        const attachments = Array.isArray(resp?.data) ? resp.data : []
        attachments.forEach((attachment, index) => {
            const content = String(attachment?.content || '').trim()
            if (!content) return
            const byteCharacters = atob(content)
            const byteNumbers = new Array(byteCharacters.length)
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i)
            }
            const byteArray = new Uint8Array(byteNumbers)
            const blob = new Blob([byteArray], { type: 'application/pdf' })
            const objectUrl = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = objectUrl
            link.download = attachment?.name || `allocation_${index + 1}.pdf`
            document.body.appendChild(link)
            window.setTimeout(() => {
                link.click()
                link.remove()
                window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
            }, 200 * (index + 1))
        })
    }

    const emailInvoice = async (invoice) => {
        if ((invoice?.status || '') === 'Voided') {
            toast.error('Voided invoices cannot be sent.')
            return
        }
        const currentClient = clients.find(client => client.id === invoice?.client?.id)
        const recipientEmail = currentClient?.invoiceEmail || invoice?.client?.email || ''
        const recipientName = currentClient?.name || invoice?.client?.name || invoice?.clientName || 'there'
        const organisationName = activeOrgInfo?.name || activeOrg?.name || 'Organisation'
        const organisationLogoUrl = typeof activeOrgInfo?.logo === 'string'
            ? activeOrgInfo.logo
            : activeOrgInfo?.logo?.url || ''
        closeInvoiceEmailDraft()
        setInvoiceEmailDraft({
            invoice,
            recipientEmail: String(recipientEmail).trim(),
            ccEmails: currentClient?.invoiceCcEmails || invoice?.clientEmailSettings?.cc || '',
            subject: `Invoice ${invoice?.invoiceNumber || ''} - ${organisationName}`,
            body: `Hi ${recipientName},\n\nPlease find attached Invoice ${invoice?.invoiceNumber || ''} along with the supporting timesheet/s.\n\nPlease review and let us know if you have any questions or require any further information.\n\nKind regards,\n${organisationName}`,
            organisationLogoUrl,
            allocationAttachments: [],
            attachmentsLoading: true,
            attachmentsError: '',
        })
        try {
            const resp = await postWithAuth('fetchInvoiceAllocationPdfs', {
                id: invoice.id,
                orgId: activeOrg.id,
            })
            if (resp?.err) throw new Error(resp.err)
            const allocationAttachments = (Array.isArray(resp?.data) ? resp.data : []).map((attachment, index) => {
                const url = pdfAttachmentToObjectUrl(attachment)
                if (url) emailAttachmentObjectUrlsRef.current.push(url)
                return {
                    name: attachment?.name || `allocation_${index + 1}.pdf`,
                    category: 'Allocation timesheet',
                    url,
                }
            })
            setInvoiceEmailDraft(current => current?.invoice?.id === invoice.id
                ? { ...current, allocationAttachments, attachmentsLoading: false }
                : current)
        } catch (error) {
            setInvoiceEmailDraft(current => current?.invoice?.id === invoice.id
                ? { ...current, attachmentsLoading: false, attachmentsError: error?.message || 'Unable to load supporting timesheets.' }
                : current)
        }
    }

    const sendReviewedInvoice = () => {
        const recipientEmail = String(invoiceEmailDraft?.recipientEmail || '').trim()
        const subject = String(invoiceEmailDraft?.subject || '').trim()
        const ccEmails = splitEmailAddresses(invoiceEmailDraft?.ccEmails)
        if (!invoiceEmailDraft?.invoice?.id || !String(invoiceEmailDraft?.body || '').trim()) {
            toast.error('Enter an email body before sending.')
            return
        }
        if (!isValidEmailAddress(recipientEmail)) {
            toast.error('Enter a valid recipient email address.')
            return
        }
        if (!subject) {
            toast.error('Enter an email subject before sending.')
            return
        }
        if (ccEmails.some(email => !isValidEmailAddress(email))) {
            toast.error('Enter valid CC email addresses separated by commas.')
            return
        }
        setShouldUploadAndSendInvoice(true)
        setChosenInvoice({
            ...invoiceEmailDraft.invoice,
            emailBody: invoiceEmailDraft.body,
            emailSubject: subject,
            recipientEmail,
            ccEmails: ccEmails.join(', '),
        })
        closeInvoiceEmailDraft()
    }

    const markInvoiceSent = async () => {
        if (!invoicePendingSent?.id || markingSent) return
        setMarkingSent(true)
        try {
            const response = await postWithAuth('markInvoiceSent', { id: invoicePendingSent.id, orgId: activeOrg.id })
            if (response?.err) throw new Error(response.err)
            if (response?.data?.id !== invoicePendingSent.id) throw new Error('Unable to confirm invoice status.')
            setInvoicePendingSent(null)
            setSuccessMessage('Invoice marked as sent. No email was sent.')
            await fetchInvoices()
        } catch (error) {
            toast.error(error.message || 'Unable to mark invoice as sent.')
        } finally {
            setMarkingSent(false)
        }
    }

    const voidInvoice = async () => {
        if (!invoicePendingVoid?.id) return
        try {
            setLoading(true)
            setLoadingMessage('Moving invoice to voided invoices')
            const resp = await postWithAuth('voidInvoice', {
                id: invoicePendingVoid.id,
                orgId: activeOrg.id,
                reason: voidReason.trim(),
            })
            if (resp?.err) {
                toast.error(resp.err)
                return
            }
            setSuccessMessage('Invoice moved to Voided Invoices')
            setInvoicePendingVoid(null)
            setVoidReason('')
            await fetchInvoices()
        } catch (error) {
            toast.error(error?.message || 'Unable to void invoice.')
        } finally {
            setLoading(false)
            setLoadingMessage('')
        }
    }

    const duplicateInvoice = async () => {
        if (!invoicePendingDuplicate?.id) return
        try {
            setLoading(true)
            setLoadingMessage('Duplicating invoice')
            const resp = await postWithAuth('duplicateInvoice', {
                id: invoicePendingDuplicate.id,
                orgId: activeOrg.id,
            })
            if (resp?.err) {
                toast.error(resp.err)
                return
            }
            const duplicatedInvoice = resp?.data
            if (!duplicatedInvoice?.id) throw new Error('The duplicated invoice was not returned.')
            setInvoicePendingDuplicate(null)
            closeInvoiceReview()
            await fetchInvoices()
            openInvoiceEditor(duplicatedInvoice)
            setSuccessMessage(`Invoice ${duplicatedInvoice.invoiceNumber} created as a ${duplicatedInvoice.worker ? 'created invoice' : 'draft'}.`)
        } catch (error) {
            toast.error(error?.message || 'Unable to duplicate invoice.')
        } finally {
            setLoading(false)
            setLoadingMessage('')
        }
    }

    const generateClientStatement = async (action) => {
        if (!statementClientId) {
            toast.error('Select a client.')
            return
        }
        try {
            setStatementBusy(true)
            const resp = await postWithAuth('generateClientStatement', {
                orgId: activeOrg.id,
                clientId: statementClientId,
                statementType,
                action,
            })
            if (resp?.err) {
                toast.error(resp.err)
                return
            }
            if (action === 'email') {
                setSuccessMessage(`Statement sent to ${resp?.data?.recipient || 'client'}`)
                return
            }
            const objectUrl = pdfAttachmentToObjectUrl({ content: resp?.data?.content })
            if (!objectUrl) throw new Error('Statement PDF was empty.')
            const link = document.createElement('a')
            link.href = objectUrl
            link.download = resp?.data?.fileName || 'client_statement.pdf'
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
            setSuccessMessage(`Statement generated for ${resp?.data?.invoiceCount || 0} invoices`)
        } catch (error) {
            toast.error(error?.message || 'Unable to generate statement.')
        } finally {
            setStatementBusy(false)
        }
    }

    const sendPaymentReminder = async (invoice) => {
        try {
            setLoading(true)
            setLoadingMessage('Sending reminder email')
            const resp = await postWithAuth('sendInvoiceReminder', {
                id: invoice.id,
                orgId: activeOrg.id
            })

            if (resp?.err) {
                toast.error(resp.err)
                return
            }

            setSuccessMessage('Reminder sent')
            fetchInvoices()
        } catch (e) {
            toast.error(e.message)
        } finally {
            setLoading(false)
            setLoadingMessage('')
        }
    }


    const openMarkPaidModal = (invoice) => {
        setInvoiceMarkingPaid(invoice)
        setPaidDateValue(invoice?.paidDate ? new Date(invoice.paidDate) : new Date())
        setPaymentAmount(getInvoiceBalanceDue(invoice).toFixed(2))
        setPaymentCategory('Bank transfer')
        setPaymentDescription(invoice?.site?.name ? `Site: ${invoice.site.name}${invoice.site.address ? `, ${invoice.site.address}` : ''}` : '')
        setSendPaymentReceipt(false)
        setShowMarkPaidModal(true)
    }

    const markInvoicePaid = async (id, paidDate) => {
        try {
            setLoading(true)
            setLoadingMessage('Recording payment')
            const resp = await postWithAuth('markInvoicePaid', {
                id,
                paidDate: paidDate ? new Date(paidDate).toISOString() : new Date().toISOString(),
                amount: Number(paymentAmount),
                category: paymentCategory,
                description: paymentDescription.trim(),
                sendReceipt: sendPaymentReceipt,
                orgId: activeOrg.id
            })
            if (resp?.err) {
                toast.error(resp.err)
                return
            }
            const result = resp.data || {}
            setSuccessMessage(result.receiptError
                ? { title: 'Payment saved', message: `Payment saved, but receipt failed: ${result.receiptError}`, tone: 'warning' }
                : (result.status === 'Paid' ? 'Final payment recorded. Invoice paid.' : `Part payment recorded. Balance ${formatCurrency(result.remainingBalance)}.`))
            const updatedInvoices = await fetchInvoices()
            if (reviewInvoice?.id === id) {
                const updatedInvoice = (updatedInvoices || []).find(invoice => invoice.id === id)
                if (updatedInvoice) setReviewInvoice(updatedInvoice)
            }
            setShowMarkPaidModal(false)
            setInvoiceMarkingPaid(null)
        } catch (error) {
            toast.error(error?.message || 'Unable to record payment.')
        } finally {
            setLoadingMessage('')
            setLoading(false)
        }
    }


    const clearJobRateFields = () => {
        setAdditionalJobDescription('')
        // setAdditionalJobGst(0)
        setAdditionalJobRate('')
        // setAdditionalJobTotal(0)
        setAdditionalJobTime('')
        setAdditionalJobQuantity('')
        setAdditionalJobQuantityType('Hours')
        setAdditionalJobGstEnabled(true)
        setAdditionalJobTextOnly(false)
    }

    const addFavouriteLineItem = (favourite) => {
        const textOnly = !!favourite.textOnly
        const rate = textOnly ? 0 : Number(favourite.rate || 0)
        const quantity = textOnly ? 0 : Number(favourite.quantity || 0)
        const subtotal = rate * quantity
        const gst = textOnly || Number(favourite.gst || 0) <= 0 ? 0 : subtotal * 0.1
        const nextRow = {
            id: `manual-${Date.now()}-${randomString()}`,
            description: String(favourite.description || '').trim(),
            textOnly,
            rate,
            quantity,
            quantityType: textOnly ? 'Unit' : (favourite.quantityType || 'Hours'),
            time: '',
            subtotal,
            gst,
            total: subtotal + gst,
        }
        setManualLineItems(current => [...current, nextRow])
        setShowInvoiceFavourites(false)
        setSuccessMessage('Favourite line item added.')
    }

    const closeInvoicePreview = () => {
        if (invoicePreviewUrl) URL.revokeObjectURL(invoicePreviewUrl)
        setInvoicePreviewUrl('')
        setInvoicePreviewFailed(false)
        setInvoicePreview(null)
    }

    const openInvoicePreview = (invoice) => {
        if (invoicePreviewUrl) URL.revokeObjectURL(invoicePreviewUrl)
        setInvoicePreviewUrl('')
        setInvoicePreviewFailed(false)
        setInvoicePreview(invoice)
    }


    const calculateAdditionalJobRowSubTotal = (row) => {
        let totalRow = 0
        if (row) {
            totalRow = parseFloat(row.rate || 0) * parseFloat(row.quantity || 0)
        } else {
            totalRow = parseFloat(additionalJobRate || 0) * parseFloat(additionalJobQuantity || 0)
        }

        if (totalRow >= 0) {
            return totalRow
        }
        return 0
    }

    const calculateAdditionalJobRowGst = (row) => {
        if (row) return Number(row.gst || 0)
        return additionalJobGstEnabled ? calculateAdditionalJobRowSubTotal() * 0.1 : 0
    }

    const handleSaveEditAdditionalJob = () => {
        if (!String(additionalJobDescription || '').trim()) {
            toast.error('Description is required.')
            return
        }
        if (!additionalJobTextOnly && (!Number.isFinite(Number(additionalJobRate)) || Number(additionalJobRate) < 0 || !Number.isFinite(Number(additionalJobQuantity)) || Number(additionalJobQuantity) < 0)) {
            toast.error('Rate and quantity must be valid positive values.')
            return
        }
        const resolvedAdditionalJobTime = parseInvoiceLineDateValue(additionalJobTime)
        const sourceRow = showEditingPrimaryJob ? invoiceJobs[showAdditionalJobIndex] : manualLineItems[showAdditionalJobIndex]
        const subtotal = additionalJobTextOnly ? 0 : calculateAdditionalJobRowSubTotal()
        const gst = additionalJobTextOnly ? 0 : calculateAdditionalJobRowGst()
        const nextRow = { description: additionalJobDescription.trim(), textOnly: additionalJobTextOnly, rate: additionalJobTextOnly ? 0 : Number(additionalJobRate), quantity: additionalJobTextOnly ? 0 : Number(additionalJobQuantity), quantityType: additionalJobTextOnly ? 'Unit' : additionalJobQuantityType, time: resolvedAdditionalJobTime ? updateInvoiceLineDate(sourceRow?.time, resolvedAdditionalJobTime) : '', gst, total: subtotal + gst, subtotal }
        if (showEditingPrimaryJob) {
            setInvoiceJobs(invoiceJobs.map((r, idx) => idx === showAdditionalJobIndex ? { ...r, ...nextRow } : r))
        } else if (showAdditionalJobIndex >= manualLineItems.length) {
            // New rows are only added once saved, so cancelling never leaves a blank placeholder behind.
            setManualLineItems([...manualLineItems, nextRow])
        } else {
            setManualLineItems(manualLineItems.map((r, idx) => idx === showAdditionalJobIndex ? { ...r, ...nextRow } : r))
        }
        setShowEditAdditionalJob(false)
        setShowEditingPrimaryJob(false)
    }

    const handleEditJob = (row, index, isPrimaryJob = false) => {
        setAdditionalJobDescription(row.description)
        // setAdditionalJobGst()
        // setAdditionalJobTotal(0)
        setAdditionalJobRate(row.rate)
        setAdditionalJobTime(parseInvoiceLineDateValue(row.time) || '')
        setAdditionalJobQuantity(row.quantity)
        setAdditionalJobQuantityType(getInvoiceQuantityType(row))
        setAdditionalJobGstEnabled(Number(row.gst || 0) > 0)
        setAdditionalJobTextOnly(!!row.textOnly)
        setShowAdditionalJobIndex(index)
        setShowEditingPrimaryJob(isPrimaryJob)
        setShowEditAdditionalJob(true)
    }

    // should update invoice subtotal, total and gst records. Then when user clicks send will generate invoice and send to owner.
    const handleSaveAdditionalJobs = async (options = {}) => {
        if (!additionalJobsModifyingInvoice?.id || loading || uploading) return
        const isWorkerInvoice = !!additionalJobsModifyingInvoice?.worker
        const payload = {
            id: additionalJobsModifyingInvoice.id,
            date: invoiceDate?.toISOString(),
            dueDate: clientDueDate?.toISOString(),
            dueTerm: clientDueTerm,
            dueDays: Number(clientDueDays || 7),
            poNumber: purchaseOrderNumber.trim(),
            jobs: invoiceJobs,
            additionalJobs: [],
            manualLineItems,
            attachments: invoiceAttachments,
            timesheetAttachments,
            autoReminderEnabled,
            discountAmount: Number(discountAmount || 0),
            discountDescription: discountDescription.trim() || 'Discount',
            discountGstEnabled,
        }


        try {
            setLoading(true)
            setLoadingMessage('Saving invoice')

            const resp = await postWithAuth(isWorkerInvoice ? 'updateWorkerInvoiceByAdmin' : 'updateClientsInvoice', {
                ...payload,
                orgId: activeOrg.id
            })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                if (!isWorkerInvoice) {
                    setClientInvoiceSortDates(current => ({
                        ...current,
                        [payload.id]: current[payload.id] ?? additionalJobsModifyingInvoice.date ?? 0,
                    }))
                }
                setShowAdditionalJobs(false)
                closeInvoiceReview()
                setSuccessMessage('Invoice saved')
                const refreshedInvoices = await fetchInvoices()
                if (options.reviewEmail) {
                    const savedInvoice = refreshedInvoices?.find(invoice => invoice.id === payload.id)
                    if (!savedInvoice) {
                        toast.error('Invoice saved, but the latest version could not be loaded for email. Please reopen the invoice and try again.')
                        return
                    }
                    await emailInvoice(savedInvoice)
                }
            }
        } catch (e) {
            toast.error(e.message)
        } finally {
            setLoading(false)
            setLoadingMessage('')
        }



    }

    const runManualInvoiceGeneration = async (regenerateExisting = false) => {
        try {
            setInvoiceGenerationMessage('')
            setLoading(true)
            setLoadingMessage('Generating invoices')
            const resp = await postWithAuth('generateManualClientInvoices', {
                orgId: activeOrg.id,
                startDate: format(manualInvoiceStartDate, 'yyyy-MM-dd'),
                endDate: format(manualInvoiceEndDate, 'yyyy-MM-dd'),
                clientId: manualInvoiceClientId,
                siteId: manualInvoiceSiteId,
                regenerateExisting,
            })

            if (resp?.err) {
                toast.error(resp.err)
                return
            }

            const data = resp?.data || {}
            const createdCount = Number(data.createdCount || 0)
            const regeneratedCount = Number(data.regeneratedCount || 0)
            const skippedCount = Number(data.skippedCount || 0)
            if (!createdCount && !regeneratedCount && !skippedCount) {
                setInvoiceGenerationMessage('No invoices found for the selected period.')
            } else {
                setInvoiceGenerationMessage(`Invoices processed. Created: ${createdCount}, Regenerated: ${regeneratedCount}, Skipped: ${skippedCount}.`)
            }
            setInvoiceGenerationPreview(null)
            fetchInvoices()
        } catch (e) {
            toast.error(e.message)
        } finally {
            setLoading(false)
            setLoadingMessage('')
        }
    }

    const previewManualInvoiceGeneration = async () => {
        if (!manualInvoiceStartDate || !manualInvoiceEndDate) {
            toast.error('Choose a start date and end date first.')
            return
        }
        if (isAfter(startOfDay(manualInvoiceStartDate), endOfDay(manualInvoiceEndDate))) {
            toast.error('Start date must be before end date.')
            return
        }
        try {
            setLoading(true)
            setLoadingMessage('Checking existing invoices')
            const resp = await postWithAuth('previewManualClientInvoices', {
                orgId: activeOrg.id,
                startDate: format(manualInvoiceStartDate, 'yyyy-MM-dd'),
                endDate: format(manualInvoiceEndDate, 'yyyy-MM-dd'),
                clientId: manualInvoiceClientId,
                siteId: manualInvoiceSiteId,
            })
            if (resp?.err) {
                toast.error(resp.err)
                return
            }
            const data = resp?.data || {}
            if (!Number(data.candidateCount || 0)) {
                showConfirmation('No invoiceable timesheets were found for the selected period.', { tone: 'info' })
                return
            }
            if (!Array.isArray(data.existingInvoices) || data.existingInvoices.length === 0) {
                await runManualInvoiceGeneration(false)
                return
            }
            setInvoiceGenerationPreview({ ...data, type: 'client' })
        } catch (e) {
            toast.error(e.message)
        } finally {
            setLoading(false)
            setLoadingMessage('')
        }
    }

    const createBlankClientInvoice = async () => {
        if (!manualInvoiceClientId || !manualInvoiceSiteId) {
            toast.error('Select a client and site before creating an invoice.')
            return
        }
        try {
            setLoading(true)
            setLoadingMessage('Creating draft invoice')
            const resp = await postWithAuth('createBlankClientInvoice', {
                orgId: activeOrg.id,
                clientId: manualInvoiceClientId,
                siteId: manualInvoiceSiteId,
            })
            if (resp?.err) {
                toast.error(resp.err)
                return
            }
            const createdInvoice = resp?.data
            if (!createdInvoice?.id) {
                toast.error('Invoice could not be created.')
                return
            }
            openInvoiceEditor(createdInvoice)
            await fetchInvoices()
            setSuccessMessage(`Invoice ${createdInvoice.invoiceNumber} created as a draft.`)
        } catch (e) {
            toast.error(e.message || 'Error creating invoice')
        } finally {
            setLoading(false)
            setLoadingMessage('')
        }
    }

    const runManualWorkerInvoiceGeneration = async (regenerateExisting = false) => {
        try {
            setInvoiceGenerationMessage('')
            setLoading(true)
            setLoadingMessage('Generating worker invoices')
            const resp = await postWithAuth('generateManualWorkerInvoices', {
                orgId: activeOrg.id,
                startDate: format(manualWorkerInvoiceStartDate, 'yyyy-MM-dd'),
                endDate: format(manualWorkerInvoiceEndDate, 'yyyy-MM-dd'),
                workerId: manualInvoiceWorkerId,
                regenerateExisting,
            })
            if (resp?.err) { toast.error(resp.err); return }
            const data = resp?.data || {}
            setInvoiceGenerationMessage(`Worker invoices processed. Created: ${Number(data.createdCount || 0)}, Regenerated: ${Number(data.regeneratedCount || 0)}, Skipped: ${Number(data.skippedCount || 0)}.`)
            setInvoiceGenerationPreview(null)
            await fetchInvoices()
        } catch (error) {
            toast.error(error?.message || 'Error generating worker invoices')
        } finally {
            setLoading(false)
            setLoadingMessage('')
        }
    }

    const previewManualWorkerInvoiceGeneration = async () => {
        if (!manualWorkerInvoiceStartDate || !manualWorkerInvoiceEndDate) {
            toast.error('Choose a start date and end date first.'); return
        }
        if (isAfter(startOfDay(manualWorkerInvoiceStartDate), endOfDay(manualWorkerInvoiceEndDate))) {
            toast.error('Start date must be before end date.'); return
        }
        try {
            setLoading(true)
            setLoadingMessage('Checking existing worker invoices')
            const resp = await postWithAuth('previewManualWorkerInvoices', {
                orgId: activeOrg.id,
                startDate: format(manualWorkerInvoiceStartDate, 'yyyy-MM-dd'),
                endDate: format(manualWorkerInvoiceEndDate, 'yyyy-MM-dd'),
                workerId: manualInvoiceWorkerId,
            })
            if (resp?.err) { toast.error(resp.err); return }
            const data = resp?.data || {}
            if (!Number(data.candidateCount || 0)) { showConfirmation('No completed worker timesheets were found for the selected period.', { tone: 'info' }); return }
            if (!data.existingInvoices?.length) { await runManualWorkerInvoiceGeneration(false); return }
            setInvoiceGenerationPreview({ ...data, type: 'worker' })
        } catch (error) {
            toast.error(error?.message || 'Error checking worker invoices')
        } finally {
            setLoading(false)
            setLoadingMessage('')
        }
    }

    // Restoring back from sent stage
    const handleMarkAsCreated = async (id) => {
        try {

            const resp = await postWithAuth('resetInvoiceToCreated', {
                id,
                orgId: activeOrg.id
            })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setSuccessMessage('Invoice restored to draft.')
                if (reviewInvoice?.id === id) closeInvoiceReview()
                const updatedInvoices = await fetchInvoices()
                const restoredInvoice = (updatedInvoices || []).find(invoice => invoice.id === id)
                if (restoredInvoice?.worker) {
                    openInvoiceEditor(restoredInvoice)
                } else {
                    setShowAdditionalJobs(false)
                }
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    // Invoicing (editing, voiding, payments, sending) is an admin-only workspace.
    if (!isOrgAdmin) {
        return <div>
            <Header />
            <div className='pageShell'>
                <Sidebar />
                <PageContent as="div" className='pageContent pageContentPadded invoicePageContent'>
                    <PageHeader title={<>Finance</>} subtitle={<>Finance is available to your admin team.</>} />
                </PageContent>
            </div>
        </div>
    }

    return <div>
        <Header />
        <div className='pageShell'>
            <Sidebar />
            <CompactSuccessModal success={successMessage} setSuccess={setSuccessMessage} overlayZIndex={3400} />
            <LoadingPopup visible={loading || uploading} message={loadingMessage || "Uploading document..."} />

            <PageContent as="div" className='pageContent pageContentPadded invoicePageContent'>
                <PageHeader title={<>Finance</>} subtitle={<>Invoices, payroll, quotes and reports</>} />
                <Tabs className='invoiceViewTabs' role='tablist' aria-label='Finance sections'>
                    {[
                        { key: 'Clients', label: 'Client Invoices', Icon: Building2Icon },
                        { key: 'Workers', label: 'Worker Invoices', Icon: UsersIcon },
                        { key: 'Payroll', label: 'Payroll', Icon: ReceiptIcon },
                        { key: 'Quotes', label: 'Quotes', Icon: FileTextIcon },
                        { key: 'ClientReport', label: 'Reports', Icon: ReceiptIcon },
                    ].map(({ key, label, Icon }) => (
                        <Tabs.Tab active={activeTab === key}
                            key={key}
                            type='button'
                            role='tab'
                            aria-selected={activeTab === key}
                            className={activeTab === key ? 'invoiceViewTab invoiceViewTabActive' : 'invoiceViewTab'}
                            onClick={() => { setActiveTab(key); if (activeTab === 'Quotes' && key !== 'Quotes') fetchInvoices() }}
                        >
                            <span className='invoiceViewTabIcon' aria-hidden='true'><Icon /></span>
                            <span>{label}</span>
                        </Tabs.Tab>
                    ))}
                </Tabs>

                <ModalComponent customMaxWidth={'1000px'} customContentStyle={{ maxHeight: '85vh', overflowY: 'auto' }} showModal={!!showAdditionalJobs} setShowModal={setShowAdditionalJobs} >
                    <div className='invoiceEditHeaderBanner'>
                        <h2 className="invoiceEditSectionHeading invoiceDetailsTitle">Invoice Details</h2>
                        <div className='invoiceEditHeaderActions'>
                            {additionalJobsModifyingInvoice?.client && (additionalJobsModifyingInvoice.status || 'Draft') === 'Draft' && <Button variant="soft" size="md" type="button" disabled={loading || uploading} title="Save changes and review email" onClick={() => handleSaveAdditionalJobs({ reviewEmail: true })}>Send Email</Button>}
                            <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={() => setShowAdditionalJobs(false)}>Cancel</Button>
                            <Button data-action-tone="save" variant="soft" size="md"  className='invoiceInlineSaveButton saveWithIcon' disabled={loading || uploading} onClick={handleSaveAdditionalJobs}>Save</Button>
                        </div>
                    </div>
                    <div className="invoiceEditMetaRow">
                        <div className="invoiceEditMetaField invoiceEditInvoiceNumberField">
                            <Input disabled={true} value={additionalJobsModifyingInvoice?.invoiceNumber || ''} setValue={() => { }} label={'Invoice Number'} />
                        </div>
                        <div className="invoiceEditMetaField invoiceEditClientNameField">
                            <Input disabled={true} value={additionalJobsModifyingInvoice?.client?.name || additionalJobsModifyingInvoice?.clientName || additionalJobsModifyingInvoice?.workerName || additionalJobsModifyingInvoice?.worker?.name || ''} setValue={() => { }} label={additionalJobsModifyingInvoice?.worker ? 'Worker' : 'Client Name'} />
                        </div>
                        {!additionalJobsModifyingInvoice?.worker && <div className="invoiceEditMetaField invoiceEditSiteField">
                            <Input disabled={true} value={additionalJobsModifyingInvoice?.site?.name || additionalJobsModifyingInvoice?.siteName || ''} setValue={() => { }} label={'Site'} />
                        </div>}
                        {!additionalJobsModifyingInvoice?.worker && <div className="invoiceEditMetaField">
                            <Input disabled={false} value={purchaseOrderNumber} setValue={setPurchaseOrderNumber} label={'PO Number'} placeholder={'Enter PO number'} />
                        </div>}
                        {!additionalJobsModifyingInvoice?.worker && <div className="invoiceEditMetaField invoiceEditInvoiceDateField">
                            <MyCalendar disabled={false} showDateOnly showYearDropdown noMaxDate startDate={invoiceDate} setStartDate={handleInvoiceDateChange} label={'Invoice Date'} />
                        </div>}
                        {!additionalJobsModifyingInvoice?.worker && <div className="invoiceEditMetaField invoiceEditDueTermsField">
                            <Dropdown
                                label={'Due Terms'}
                                disabled={false}
                                options={dueTermOptions}
                                value={dueTermOptions.find(option => option.value === clientDueTerm) || dueTermOptions[1]}
                                setValue={value => handleClientDueTermChange(value?.value)}
                            />
                        </div>}
                        {!additionalJobsModifyingInvoice?.worker && <div className="invoiceEditMetaField invoiceEditDueDateField">
                            <MyCalendar disabled={false} showDateOnly showYearDropdown startDate={clientDueDate} setStartDate={setClientDueDate} label={'Due Date'} />
                        </div>}
                        {!additionalJobsModifyingInvoice?.worker && <FieldLabel choice className="invoiceEditReminderToggle">
                            <UiCheckbox type="checkbox" checked={!!autoReminderEnabled} onChange={e => setAutoReminderEnabled(e.target.checked)} />
                            Auto reminder for payment
                        </FieldLabel>}
                    </div>

                    <Table emptyItems="line items" className="invoiceLineItemsTable">
                        <TableHead>
                            <TableRow>
                                <TableHeaderCell>Date/Time</TableHeaderCell>
                                <TableHeaderCell>Description</TableHeaderCell>
                                <TableHeaderCell>Rate</TableHeaderCell>
                                <TableHeaderCell>Quantity</TableHeaderCell>
                                <TableHeaderCell>GST</TableHeaderCell>
                                <TableHeaderCell>Total</TableHeaderCell>
                                <TableHeaderCell>Action</TableHeaderCell>
                            </TableRow>
                        </TableHead>
                        <tbody>
                            {[...(invoiceJobs || []).map((row, index) => ({ ...row, rowType: 'job', rowIndex: index })), ...(manualLineItems || []).map((row, index) => ({ ...row, rowType: 'manual', rowIndex: index }))].map((row, index) => {
                                const lineDateTime = getInvoiceLineDateTime(row.time)
                                const lineDescription = getInvoiceLineDescription(row)
                                return <TableRow key={`row-${row.rowType}-${index}`}>
                                    <TableCell>
                                        <div className="invoiceLineDate">{lineDateTime.date}</div>
                                        {lineDateTime.time && <div className="invoiceLineTime">{lineDateTime.time}</div>}
                                    </TableCell>
                                    <TableCell>
                                        <div className="invoiceLineRole">{lineDescription.role}</div>
                                        {lineDescription.names.map((name, nameIndex) => <div className="invoiceLineWorker" key={`${name}-${nameIndex}`}>{name}</div>)}
                                    </TableCell>
                                    <TableCell>{row.textOnly ? '-' : formatCurrency(row.rate)}</TableCell>
                                    <TableCell>{row.textOnly ? '-' : formatInvoiceQuantity(row.quantity, getInvoiceQuantityType(row))}</TableCell>
                                    <TableCell>{row.textOnly ? '-' : formatCurrency(row.gst ?? calculateAdditionalJobRowGst(row))}</TableCell>
                                    <TableCell>{row.textOnly ? '-' : formatCurrency(row.total ?? (calculateAdditionalJobRowSubTotal(row) + calculateAdditionalJobRowGst(row)))}</TableCell>
                                    <TableCell>
                                        <div className="invoiceLineItemActions">
                                            <Button data-action-tone="edit" variant="secondary" size="icon"  className='recordActionIcon invoiceLineEditIcon' aria-label='Edit line item' title='Edit' onClick={() => { handleEditJob(row, row.rowIndex, row.rowType === 'job') }}><EditIcon /></Button>
                                            <Button data-action-tone="delete" variant="danger" size="icon"  className='recordActionIcon invoiceLineDeleteIcon' aria-label='Delete line item' title='Delete' onClick={() => setLineItemPendingDelete(row)}><TrashIcon /></Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            })}
                            <TableRow className="invoiceDiscountRow">
                                <TableCell></TableCell>
                                <TableCell>
                                    <UiInput className="invoiceDiscountDescription" value={discountDescription} onChange={event => setDiscountDescription(event.target.value)} aria-label="Discount description" />
                                </TableCell>
                                <TableCell>
                                    <div className="invoiceDiscountAmountField"><span>$</span><UiInput inputMode="decimal" value={discountAmount} onChange={event => setDiscountAmount(event.target.value.replace(/[^0-9.]/g, ''))} placeholder="0.00" aria-label="Discount amount" /></div>
                                </TableCell>
                                <TableCell>1 Unit</TableCell>
                                <TableCell>-{formatCurrency(invoiceEditTotals.discountGst)}</TableCell>
                                <TableCell>-{formatCurrency(invoiceEditTotals.discountAmount + invoiceEditTotals.discountGst)}</TableCell>
                                <TableCell>
                                    <FieldLabel choice className="invoiceDiscountGstToggle"><UiCheckbox type="checkbox" checked={discountGstEnabled} onChange={event => setDiscountGstEnabled(event.target.checked)} /> GST</FieldLabel>
                                </TableCell>
                            </TableRow>
                            <TableRow className="invoiceNewLineItemRow">
                                <TableCell colSpan={7}>
                                    <div className='invoiceNewLineItemGrid'>
                                        <div className='invoiceLineItemAddActions'>
                                            <Button variant="create" size="md" className='invoiceInlineLink' onClick={() => { setShowAdditionalJobIndex(manualLineItems.length); setShowEditingPrimaryJob(false); clearJobRateFields(); setShowEditAdditionalJob(true) }}>+ New Line Item</Button>
                                            <Button variant="create" size="md" className='invoiceInlineLink' onClick={() => { setShowAdditionalJobIndex(manualLineItems.length); setShowEditingPrimaryJob(false); clearJobRateFields(); setAdditionalJobTextOnly(true); setShowEditAdditionalJob(true) }}>+ Free Text</Button>
                                            <Button variant="create" size="md" className='invoiceInlineLink' onClick={() => setShowInvoiceFavourites(true)}>+ Favourites</Button>
                                        </div>
                                        <div className="invoicePaymentTotals">
                                            <strong>Subtotal</strong><span>{formatCurrency(invoiceEditTotals.subtotal)}</span>
                                            <strong>GST</strong><span>{formatCurrency(invoiceEditTotals.gst)}</span>
                                            <strong>Total</strong><strong>{formatCurrency(invoiceEditTotals.total)}</strong>
                                        </div>
                                    </div>
                                </TableCell>
                            </TableRow>
                            {!additionalJobsModifyingInvoice?.worker && <TableRow className="invoicePaymentSummaryRow">
                                <TableCell colSpan={7}>
                                    <div className="invoicePaymentSummaryGrid">
                                        <div className="invoicePaymentTerms">
                                            <strong>Payment Terms:</strong>
                                            <span>Please make payment via direct deposit to:</span>
                                            <span><b>Account Name:</b> {activeOrgInfo?.accountName || '-'}</span>
                                            <span><b>BSB:</b> {activeOrgInfo?.bsb || '-'}</span>
                                            <span><b>Acc No:</b> {activeOrgInfo?.accountNumber || '-'}</span>
                                            <span><b>Reference:</b> Use invoice number {additionalJobsModifyingInvoice?.invoiceNumber || ''}.</span>
                                        </div>
                                    </div>
                                </TableCell>
                            </TableRow>}
                        </tbody>
                    </Table>

                    <div className="invoiceEditAttachmentGrid">
                        <div className="invoiceEditDividerSection">
                            <h2 className="invoiceEditSectionHeading">Timesheets</h2>
                            <MyDropzoneMulti
                                disabled={false}
                                storageLocation={`InvoiceTimesheetAttachments/${additionalJobsModifyingInvoice?.id || randomString()}`}
                                errors={[]}
                                name={'timesheetAttachments'}
                                editing={true}
                                images={timesheetAttachments}
                                updateImages={setTimesheetAttachments}
                                setUploading={setUploading}
                                allowPdfOnly
                            />
                        </div>

                        <div className="invoiceEditDividerSection">
                            <h2 className="invoiceEditSectionHeading">Attachments</h2>
                            <MyDropzoneMulti
                                disabled={false}
                                storageLocation={`InvoiceAttachments/${additionalJobsModifyingInvoice?.id || randomString()}`}
                                errors={[]}
                                name={'invoiceAttachments'}
                                editing={true}
                                images={invoiceAttachments}
                                updateImages={setInvoiceAttachments}
                                setUploading={setUploading}
                                allowPdf
                            />
                        </div>
                    </div>

                    <InvoicePaymentHistory invoice={additionalJobsModifyingInvoice} />

                    <div className='invoiceHistoryBlock invoiceEditDividerSection'>
                        <h2 className='invoiceEditSectionHeading'>History</h2>
                        <Table emptyItems="activity entries">
                            <TableHead>
                                <TableRow>
                                    <TableHeaderCell>Date/Time</TableHeaderCell>
                                    <TableHeaderCell>Action</TableHeaderCell>
                                    <TableHeaderCell>Status</TableHeaderCell>
                                    <TableHeaderCell>By</TableHeaderCell>
                                </TableRow>
                            </TableHead>
                            <tbody>
                                {(additionalJobsModifyingInvoice?.history || []).slice().reverse().map((row, index) => (
                                    <TableRow key={`history-${index}`}>
                                        <TableCell>{row.date ? <>{format(new Date(row.date), 'EEE, d MMM yyyy')}<br />{formatTime(new Date(row.date))}</> : '-'}</TableCell>
                                        <TableCell>{row.action || row.type || '-'}{row.details && <div className="invoiceHistoryDetails">{row.details}</div>}</TableCell>
                                        <TableCell>{row.status || '-'}</TableCell>
                                        <TableCell>{formatName(row.actorName) || '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                </ModalComponent>

                <ModalComponent
                    showModal={!!lineItemPendingDelete}
                    setShowModal={setLineItemPendingDelete}
                    customMaxWidth='300px'
                    customContentStyle={{ minWidth: 0, width: 'min(300px, calc(100vw - 32px))', padding: '14px' }}
                    customOverlayStyle={{ zIndex: 3100 }}
                >
                    <div className='allocationConfirmMessage recordConfirmMessage'>
                        <h2 className='modalTitle'>Are you sure?</h2>
                        <p className='modalSubtitle'>This will remove {lineItemPendingDelete?.description || 'this line item'} from the invoice.</p>
                        <div className='modalFooter'>
                            <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={() => setLineItemPendingDelete(null)}>Cancel</Button>
                            <Button variant="danger" size="md" className='modalDangerButton' onClick={() => {
                                if (lineItemPendingDelete?.rowType === 'job') {
                                    setInvoiceJobs(invoiceJobs.filter((r, idx) => idx !== lineItemPendingDelete.rowIndex))
                                } else {
                                    setManualLineItems(manualLineItems.filter((r, idx) => idx !== lineItemPendingDelete?.rowIndex))
                                }
                                setLineItemPendingDelete(null)
                            }}>Yes</Button>
                        </div>
                    </div>
                </ModalComponent>

                <ModalComponent
                    showModal={!!showEditAdditionalJob}
                    setShowModal={setShowEditAdditionalJob}
                    customOverlayStyle={{ zIndex: 3100 }}
                >
                    <div className='invoiceModalStack'>
                        <h2 className='modalTitle'>Line Item</h2>
                        <MyCalendar disabled={false} showDateOnly showYearDropdown startDate={additionalJobTime} setStartDate={setAdditionalJobTime} label={'Date (optional)'} />

                        <Input
                            multiline
                            disabled={false}
                            value={additionalJobDescription}
                            setValue={value => setAdditionalJobDescription(String(value || '').replace(/^(\s*)([a-z])/, (_, spaces, letter) => `${spaces}${letter.toUpperCase()}`))}
                            placeholder={'Description'}
                            label={'Description'}
                        />
                        {!additionalJobTextOnly && <><div className="invoiceCurrencyField">
                            <FieldLabel className="fieldLabel">Rate</FieldLabel>
                            <span aria-hidden="true">$</span>
                            <UiInput
                                aria-label="Rate"
                                inputMode="decimal"
                                value={additionalJobRateFocused || additionalJobRate === ''
                                    ? additionalJobRate
                                    : Number(additionalJobRate || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                onFocus={() => setAdditionalJobRateFocused(true)}
                                onBlur={() => setAdditionalJobRateFocused(false)}
                                onChange={(event) => setAdditionalJobRate(event.target.value.replace(/[^0-9.]/g, ''))}
                                placeholder="0.00"
                            />
                        </div>
                        <Dropdown
                            label={'Quantity Type'}
                            disabled={false}
                            options={quantityTypeOptions}
                            value={quantityTypeOptions.find(option => option.value === additionalJobQuantityType) || quantityTypeOptions[0]}
                            setValue={option => setAdditionalJobQuantityType(option?.value || 'Hours')}
                        />
                        <Input disabled={false} type={'numeric'} value={additionalJobQuantity} setValue={setAdditionalJobQuantity} placeholder={'Quantity'} label={'Quantity'} />
                        <div className="invoiceLineItemSummaryRow">
                            <FieldLabel choice className="invoiceLineGstToggle">
                                <UiCheckbox type="checkbox" checked={additionalJobGstEnabled} onChange={(event) => setAdditionalJobGstEnabled(event.target.checked)} />
                                Apply GST (10%)
                            </FieldLabel>
                            <div className="invoiceLineItemAmounts">
                                <p className="invoiceLineItemAmount"><b>GST:</b> {formatCurrency(calculateAdditionalJobRowGst())}</p>
                                <p className="invoiceLineItemAmount"><b>Total:</b> {formatCurrency(calculateAdditionalJobRowSubTotal() + calculateAdditionalJobRowGst())}</p>
                            </div>
                        </div></>}


                        <div className='modalFooter'>
                            <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={() => { setShowEditAdditionalJob(false); clearJobRateFields() }}>Cancel</Button>
                            <Button data-action-tone="save" variant="soft" size="md"  className='invoiceInlineSaveButton saveWithIcon' disabled={false} onClick={handleSaveEditAdditionalJob}>Save</Button>
                        </div>
                    </div>
                </ModalComponent>

                <ModalComponent
                    showModal={!!showMarkPaidModal}
                    setShowModal={(show) => { setShowMarkPaidModal(show); if (!show) setInvoiceMarkingPaid(null) }}
                    customOverlayStyle={{ zIndex: 3100 }}
                >
                    <div className='invoiceMarkPaidModal'>
                        <h2 className='modalTitle'>Record Payment</h2>
                        <p className='invoiceMarkPaidMeta'>{invoiceMarkingPaid?.invoiceNumber || '-'} · Balance {formatCurrency(getInvoiceBalanceDue(invoiceMarkingPaid || {}))}</p>
                        <div className='invoicePaymentGrid'>
                            <Input type='number' inputMode='decimal' step='0.01' value={paymentAmount} setValue={setPaymentAmount} label='Amount' required />
                            <MyCalendar disabled={false} showDateOnly showYearDropdown startDate={paidDateValue} setStartDate={setPaidDateValue} label='Payment Date' />
                        </div>
                        <div className='invoicePaymentGst'>
                            <span>Includes GST</span>
                            <strong>{formatCurrency(Number(paymentAmount || 0) / 11)}</strong>
                        </div>
                        <Dropdown
                            label='Category'
                            options={paymentCategoryOptions}
                            value={paymentCategoryOptions.find(option => option.value === paymentCategory) || paymentCategoryOptions[0]}
                            setValue={option => setPaymentCategory(option?.value || '')}
                        />
                        <Input multiline value={paymentDescription} setValue={setPaymentDescription} label='Description' placeholder='Payment notes or site reference' />
                        {!!(invoiceMarkingPaid?.clientEmailSettings?.to || invoiceMarkingPaid?.client?.email) && <FieldLabel choice className='invoicePaymentReceiptToggle'><UiCheckbox type='checkbox' checked={sendPaymentReceipt} onChange={event => setSendPaymentReceipt(event.target.checked)} /> Send a receipt to {invoiceMarkingPaid.clientEmailSettings?.to || invoiceMarkingPaid.client?.email}</FieldLabel>}
                        <div className='modalFooter'>
                            <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={() => { setShowMarkPaidModal(false); setInvoiceMarkingPaid(null) }}>Cancel</Button>
                            <Button data-action-tone="save" variant="soft" size="md"  className='invoiceInlineSaveButton saveWithIcon' onClick={() => markInvoicePaid(invoiceMarkingPaid?.id, paidDateValue)} disabled={!invoiceMarkingPaid || !(Number(Number(paymentAmount).toFixed(2)) > 0) || !paymentCategory || Number(Number(paymentAmount).toFixed(2)) > Number(getInvoiceBalanceDue(invoiceMarkingPaid || {}).toFixed(2))}>Save Payment</Button>
                        </div>
                    </div>
                </ModalComponent>

                <ModalComponent
                    showModal={!!invoiceEmailDraft}
                    setShowModal={(show) => { if (!show) closeInvoiceEmailDraft() }}
                    customMaxWidth='680px'
                    customContentStyle={{ width: 'min(680px, calc(100vw - 32px))' }}
                >
                    <div className='invoiceEmailReviewModal'>
                        <h2 className='modalTitle'>Review Email</h2>
                        <p className='modalSubtitle'>Review and edit the message before sending the invoice and its supporting timesheets.</p>
                        <FieldLabel className='fieldLabel' htmlFor='invoice-email-to'>To</FieldLabel>
                        <UiInput
                            id='invoice-email-to'
                            className='invoiceEmailAddressInput'
                            type='email'
                            value={invoiceEmailDraft?.recipientEmail || ''}
                            onChange={(event) => setInvoiceEmailDraft(current => current ? { ...current, recipientEmail: event.target.value } : current)}
                            placeholder='accounts@client.com'
                        />
                        <FieldLabel className='fieldLabel' htmlFor='invoice-email-cc'>CC (optional)</FieldLabel>
                        <UiInput
                            id='invoice-email-cc'
                            className='invoiceEmailAddressInput'
                            value={invoiceEmailDraft?.ccEmails || ''}
                            onChange={(event) => setInvoiceEmailDraft(current => current ? { ...current, ccEmails: event.target.value } : current)}
                            placeholder='manager@client.com, accounts@example.com'
                        />
                        <FieldLabel className='fieldLabel' htmlFor='invoice-email-subject'>Subject</FieldLabel>
                        <UiInput
                            id='invoice-email-subject'
                            className='invoiceEmailAddressInput'
                            value={invoiceEmailDraft?.subject || ''}
                            maxLength={250}
                            onChange={(event) => setInvoiceEmailDraft(current => current ? { ...current, subject: event.target.value } : current)}
                            placeholder='Invoice subject'
                        />
                        <FieldLabel className='fieldLabel' htmlFor='invoice-email-body'>Email Body</FieldLabel>
                        <Textarea
                            id='invoice-email-body'
                            className='invoiceEmailBodyInput'
                            value={invoiceEmailDraft?.body || ''}
                            maxLength={10000}
                            onChange={(event) => setInvoiceEmailDraft(current => current ? { ...current, body: event.target.value } : current)}
                        />
                        {(invoiceEmailDraft?.organisationLogoUrl || activeOrgInfo?.name) && (
                            <div className='invoiceEmailLogoPreview'>
                                <span>Email footer</span>
                                {invoiceEmailDraft?.organisationLogoUrl
                                    ? <img src={invoiceEmailDraft.organisationLogoUrl} alt={`${activeOrgInfo?.name || 'Organisation'} logo`} />
                                    : <strong>{activeOrgInfo?.name}</strong>}
                            </div>
                        )}
                        <div className='invoiceEmailAttachments'>
                            <div className='invoiceEmailAttachmentsHeader'>
                                <span className='fieldLabel'>Attachments</span>
                                {!invoiceEmailDraft?.attachmentsLoading && !invoiceEmailDraft?.attachmentsError && (
                                    <span>{1 + Number(invoiceEmailDraft?.allocationAttachments?.length || 0) + Number(invoiceEmailDraft?.invoice?.timesheetAttachments?.length || 0) + Number(invoiceEmailDraft?.invoice?.attachments?.length || 0)} files</span>
                                )}
                            </div>
                            {invoiceEmailDraft?.attachmentsLoading && <p className='invoiceEmailAttachmentsState'>Preparing attachment list...</p>}
                            {invoiceEmailDraft?.attachmentsError && <p className='invoiceEmailAttachmentsError'>{invoiceEmailDraft.attachmentsError}</p>}
                            {!invoiceEmailDraft?.attachmentsLoading && !invoiceEmailDraft?.attachmentsError && (
                                <ul>
                                    {[
                                        { name: `${invoiceEmailDraft?.invoice?.invoiceNumber || 'Invoice'}.pdf`, category: 'Invoice', previewInvoice: true, invoice: invoiceEmailDraft?.invoice, file: invoiceEmailDraft?.invoice?.url ? { url: invoiceEmailDraft.invoice.url } : null },
                                        ...(invoiceEmailDraft?.allocationAttachments || []),
                                        ...(invoiceEmailDraft?.invoice?.timesheetAttachments || []).map((attachment, index) => ({ name: attachment?.name || `timesheet_${index + 1}.pdf`, category: 'Uploaded timesheet', file: attachment })),
                                        ...(invoiceEmailDraft?.invoice?.attachments || []).map((attachment, index) => ({ name: attachment?.name || `attachment_${index + 1}`, category: 'Attachment', file: attachment })),
                                    ].map((attachment, index) => (
                                        <li key={`${attachment.category}-${attachment.name}-${index}`}>
                                            <Button variant="secondary" size="md" type='button' onClick={() => openInvoiceEmailAttachment(attachment)}>{attachment.name}</Button>
                                            <small>{attachment.category}</small>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className='modalFooter'>
                            <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={closeInvoiceEmailDraft}>Cancel</Button>
                            <Button variant="secondary" size="md" className='invoiceInlineSaveButton' disabled={!String(invoiceEmailDraft?.recipientEmail || '').trim() || !String(invoiceEmailDraft?.subject || '').trim() || !String(invoiceEmailDraft?.body || '').trim() || uploading || invoiceEmailDraft?.attachmentsLoading || !!invoiceEmailDraft?.attachmentsError} onClick={sendReviewedInvoice}>Send Invoice</Button>
                        </div>
                    </div>
                </ModalComponent>

                <ModalComponent
                    showModal={!!invoicePendingDuplicate}
                    setShowModal={(show) => { if (!show) setInvoicePendingDuplicate(null) }}
                    customOverlayStyle={{ zIndex: 3100 }}
                >
                    <div className='allocationConfirmMessage recordConfirmMessage'>
                        <h2 className='modalTitle'>Duplicate Invoice?</h2>
                        <p className='modalSubtitle'>Create a new invoice from {invoicePendingDuplicate?.invoiceNumber || 'this invoice'}? The new invoice will have a new invoice number and no payment or delivery history.</p>
                        <div className='modalFooter'>
                            <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={() => setInvoicePendingDuplicate(null)}>Cancel</Button>
                            <Button variant="secondary" size="md" className='invoiceInlineSaveButton saveWithIcon' disabled={loading} onClick={duplicateInvoice}>Duplicate Invoice</Button>
                        </div>
                    </div>
                </ModalComponent>

                <ModalComponent showModal={!!invoicePendingSent} setShowModal={() => !markingSent && setInvoicePendingSent(null)}>
                    <h2 className='modalTitle'>Mark as sent</h2>
                    <p>Mark {invoicePendingSent?.invoiceNumber || 'this invoice'} as sent? This updates its status without sending an email.</p>
                    <div className='modalFooter'>
                        <Button variant='secondary' disabled={markingSent} onClick={() => setInvoicePendingSent(null)}>Cancel</Button>
                        <Button variant='soft' disabled={markingSent} onClick={markInvoiceSent}>{markingSent ? 'Updating…' : 'Mark as sent'}</Button>
                    </div>
                </ModalComponent>
                <ModalComponent showModal={!!invoicePendingVoid} setShowModal={setInvoicePendingVoid}>
                    <div className='invoiceMarkPaidModal'>
                        <h2 className='modalTitle'>Void Invoice</h2>
                        <p className='modalSubtitle'>Move {invoicePendingVoid?.invoiceNumber || 'this invoice'} to Voided Invoices? It will no longer be editable, sendable, payable, or included in statements.</p>
                        <Input disabled={false} value={voidReason} setValue={setVoidReason} label={'Reason (optional)'} placeholder={'Reason for voiding'} />
                        <div className='modalFooter'>
                            <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={() => { setInvoicePendingVoid(null); setVoidReason('') }}>Cancel</Button>
                            <Button variant="danger" size="md" className='modalDangerButton' onClick={voidInvoice}>Void Invoice</Button>
                        </div>
                    </div>
                </ModalComponent>

                <ModalComponent
                    showModal={!!invoiceGenerationPreview}
                    setShowModal={setInvoiceGenerationPreview}
                    customMaxWidth='720px'
                    customContentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
                >
                    <div className='invoiceGenerationModal'>
                        <h2 className='modalTitle'>Existing invoices found</h2>
                        <p className='modalSubtitle'>These invoices already exist for the selected period. You can regenerate them using the same invoice numbers, or skip them and only create missing invoices.</p>
                        <div className='invoiceGenerationSummary'>
                            <span>Candidate invoices: {invoiceGenerationPreview?.candidateCount || 0}</span>
                            <span>New only: {invoiceGenerationPreview?.newInvoiceCount || 0}</span>
                            <span>Existing: {invoiceGenerationPreview?.existingInvoices?.length || 0}</span>
                        </div>
                        <Table emptyItems="invoices" className='invoiceHistoryTable invoiceListTable invoiceGenerationPreviewTable'>
                            <TableHead>
                                <TableRow>
                                    <TableHeaderCell>Invoice</TableHeaderCell>
                                    <TableHeaderCell>{invoiceGenerationPreview?.type === 'worker' ? 'Worker' : 'Client'}</TableHeaderCell>
                                    {invoiceGenerationPreview?.type !== 'worker' && <TableHeaderCell>Site</TableHeaderCell>}
                                    <TableHeaderCell>Status</TableHeaderCell>
                                    <TableHeaderCell>Total</TableHeaderCell>
                                </TableRow>
                            </TableHead>
                            <tbody>
                                {(invoiceGenerationPreview?.existingInvoices || []).map((invoice) => {
                                    const statusMeta = getInvoiceStatusMeta(invoice)
                                    return (
                                        <TableRow flagged={invoice.status === 'Overdue'} key={invoice.id}>
                                            <TableCell>{invoice.invoiceNumber || '-'}</TableCell>
                                            <TableCell>{invoiceGenerationPreview?.type === 'worker' ? (formatName(invoice.workerName) || '-') : (invoice.clientName || '-')}</TableCell>
                                            {invoiceGenerationPreview?.type !== 'worker' && <TableCell>{invoice.siteName || '-'}</TableCell>}
                                            <TableCell>
                                                <span className={`invoiceStatusPill invoiceStatusPill${statusMeta.tone.charAt(0).toUpperCase() + statusMeta.tone.slice(1)}`}>
                                                    {invoice.status || 'Draft'}
                                                </span>
                                            </TableCell>
                                            <TableCell>{formatCurrency(invoice.total)}</TableCell>
                                        </TableRow>
                                    )
                                })}
                            </tbody>
                        </Table>
                        <div className='modalFooter'>
                            <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={() => setInvoiceGenerationPreview(null)}>Cancel</Button>
                            <Button variant="secondary" size="md" className='secondaryButton' onClick={() => invoiceGenerationPreview?.type === 'worker' ? runManualWorkerInvoiceGeneration(false) : runManualInvoiceGeneration(false)}>Skip Existing</Button>
                            <Button variant="secondary" size="md" className='invoiceInlineSaveButton saveWithIcon' onClick={() => invoiceGenerationPreview?.type === 'worker' ? runManualWorkerInvoiceGeneration(true) : runManualInvoiceGeneration(true)}>Regenerate Existing</Button>
                        </div>
                    </div>
                </ModalComponent>

                {activeTab === 'Clients' ? <>
                    <SectionCard title={<>Generate Invoices</>} className='sectionCard width-100 invoiceManualGenerationCard' subtitle={<>Generate from timesheets for a date range, client and optional site, or create a standalone draft invoice.</>} bodyClassName="sectionBody sectionCardBody">

                            <div className='invoiceManualGenerationBlock'>

                                <div className='invoiceManualGenerationGrid'>
                                    <div className='invoiceFilterItem'>
                                        <MyCalendar disabled={false} showDateOnly showYearDropdown startDate={manualInvoiceStartDate} setStartDate={setManualInvoiceStartDate} label={'Start Date'} />
                                    </div>
                                    <div className='invoiceFilterItem'>
                                        <MyCalendar disabled={false} showDateOnly showYearDropdown startDate={manualInvoiceEndDate} setStartDate={setManualInvoiceEndDate} label={'End Date'} />
                                    </div>
                                    <div className='invoiceFilterItem'>
                                        <Dropdown
                                            label={'Client'}
                                            options={manualInvoiceClientOptions}
                                            value={manualInvoiceClientOptions.find(option => option.value === manualInvoiceClientId) || manualInvoiceClientOptions[0]}
                                            setValue={value => {
                                                setManualInvoiceClientId(value?.value || '')
                                                setManualInvoiceSiteId('')
                                            }}
                                        />
                                    </div>
                                    <div className='invoiceFilterItem'>
                                        <Dropdown
                                            label={'Site'}
                                            options={manualInvoiceSiteOptions}
                                            value={manualInvoiceSiteOptions.find(option => option.value === manualInvoiceSiteId) || manualInvoiceSiteOptions[0]}
                                            setValue={value => setManualInvoiceSiteId(value?.value || '')}
                                        />
                                    </div>

                                </div><div className='invoiceManualGenerationAction invoiceGenerationActions'>
                                        <Button variant="create" size="md" className='invoiceInlineSaveButton saveWithIcon' onClick={previewManualInvoiceGeneration}>Generate Invoices</Button>
                                        <Button variant="create" size="md" className='secondaryButton' disabled={!manualInvoiceClientId || !manualInvoiceSiteId} onClick={createBlankClientInvoice}>Create Blank Invoice</Button>
                                    </div>
                                {invoiceGenerationMessage && (
                                    <div className='invoiceGenerationPageMessage' role='status'>
                                        {invoiceGenerationMessage}
                                    </div>
                                )}
                            </div>

                    </SectionCard>
                    <SectionCard loadingRequests={['fetchInvoices']} className='sectionCard width-100' bodyClassName="sectionBody sectionCardBody">

                            <div className="invoiceListShell">
                                <div className="invoiceSummaryGrid invoiceSummaryGridFour">
                                    <StatCard icon={<ReceiptIcon />} label={<>Outstanding Amount</>} value={<>{formatCurrency(clientInvoiceSummary.outstandingAmount)}</>} tone="blue" />
                                    <StatCard icon={<ReceiptIcon />} label={<>Draft Invoice Amount</>} value={<>{formatCurrency(clientInvoiceSummary.draftAmount)}</>} tone="orange" />
                                    <StatCard icon={<ReceiptIcon />} label={<>Overdue Amount</>} value={<>{formatCurrency(clientInvoiceSummary.overdueAmount)}</>} tone="red" />
                                    <StatCard icon={<ReceiptIcon />} label={<>Overdue Invoices</>} value={<>{clientInvoiceSummary.overdueCount}</>} tone="red" />
                                </div>
                            </div>

                    </SectionCard>

                    <Section title={'Client Invoices'} className='width-100' variant='card'>
                        <div className="invoiceListShell">
                            <Tabs className='invoiceLedgerTabs' role='tablist' aria-label='Invoice lists'>
                                <Tabs.Tab active={clientInvoiceView === 'active'} type='button' role='tab' aria-selected={clientInvoiceView === 'active'} data-ledger-tone='active' className={clientInvoiceView === 'active' ? 'invoiceLedgerTab invoiceLedgerTabActive' : 'invoiceLedgerTab'} onClick={() => { setClientInvoiceView('active'); setSelectedStatusFilter('all') }}><span className='invoiceLedgerTabIcon' aria-hidden='true'><FileTextIcon /></span><span>Active Invoices</span></Tabs.Tab>
                                <Tabs.Tab active={clientInvoiceView === 'voided'} type='button' role='tab' aria-selected={clientInvoiceView === 'voided'} data-ledger-tone='voided' className={clientInvoiceView === 'voided' ? 'invoiceLedgerTab invoiceLedgerTabActive' : 'invoiceLedgerTab'} onClick={() => { setClientInvoiceView('voided'); setSelectedStatusFilter('all') }}><span className='invoiceLedgerTabIcon' aria-hidden='true'><ArchiveIcon /></span><span>Voided Invoices</span></Tabs.Tab>
                            </Tabs>
                            <div className="invoiceFilterBar clientInvoiceFilterBar">
                                {clientInvoiceView === 'active' && <div className="invoiceFilterItem">
                                    <Dropdown
                                        options={statusFilterOptions}
                                        value={selectedStatusFilterOption}
                                        setValue={value => setSelectedStatusFilter(value?.value || 'all')}
                                    />
                                </div>}
                                <div className="invoiceFilterItem">
                                    <Dropdown
                                        options={dateFilterOptions}
                                        value={selectedDateFilterOption}
                                        setValue={value => setSelectedDateFilter(value?.value || 'all')}
                                    />
                                </div>
                                {selectedDateFilter === 'custom' && (
                                    <>
                                        <div className="invoiceFilterItem">
                                            <MyCalendar disabled={false} showDateOnly showYearDropdown startDate={customDateFrom} setStartDate={setCustomDateFrom} label={'From'} />
                                        </div>
                                        <div className="invoiceFilterItem">
                                            <MyCalendar disabled={false} showDateOnly showYearDropdown startDate={customDateTo} setStartDate={setCustomDateTo} label={'To'} />
                                        </div>
                                    </>
                                )}
                                <div className="invoiceFilterItem invoiceExportBatchItem">
                                    <Button variant="secondary" size="md" className="secondaryButton" onClick={downloadClientInvoiceReport}>Export Report</Button>
                                </div>
                                <div className="invoiceSearchRow">
                                    <div className="invoiceSearchItem dashboardSearchField uiSearchContainer">

                                        <SearchInput
                                            className="invoiceSearchInput"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder="Search"
                                        />
                                    </div>
                                </div>
                            </div>

                            <Table emptyItems="invoices" className="invoiceListTable">
                                <TableHead>
                                    <TableRow>
                                        <TableHeaderCell>
                                            Invoice Number
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            Client
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            Description
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            Date issued
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            Date paid
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            Total
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            Status
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            Action
                                        </TableHeaderCell>
                                    </TableRow>
                                </TableHead>
                                <tbody>
                                    {paginatedClientInvoices.map(invoice => {
                                        const statusMeta = getInvoiceStatusMeta(invoice)
                                        return <TableRow flagged={invoice.status === 'Overdue'} key={invoice.id}>
                                                <TableCell data-label="Invoice Number" className="invoiceNumberCell hover" onClick={() => openInvoiceReview(invoice)}>{invoice.invoiceNumber}</TableCell>
                                                <TableCell data-label="Client">{invoice.clientName}</TableCell>
                                                <TableCell data-label="Description">{getClientInvoiceDescription(invoice)}</TableCell>
                                                <TableCell data-label="Date issued">{invoice.date ? format(new Date(invoice.date), 'EEE, d MMM yyyy') : '-'}</TableCell>
                                                <TableCell data-label="Date paid">{invoice.paidDate ? format(new Date(invoice.paidDate), 'EEE, d MMM yyyy') : '-'}</TableCell>
                                                <TableCell data-label="Total" className="invoiceTotalCell">{formatCurrency(invoice.total)}</TableCell>
                                                <TableCell data-label="Status"><span className={`invoiceStatusPill invoiceStatusPill${statusMeta.tone.charAt(0).toUpperCase() + statusMeta.tone.slice(1)}`}>{statusMeta.label}</span></TableCell>
                                                <TableCell data-label="Action">
                                                    <ActionMenu className="invoiceRowActions">
                                                        {(invoice.status === 'Draft' || (invoice.deliveryChangesPending && !['Paid', 'Voided'].includes(invoice.status))) && <Button variant='secondary' size='icon' className='recordActionIcon' title='Mark as sent' aria-label='Mark as sent' onClick={() => setInvoicePendingSent(invoice)}><CheckIcon /></Button>}
                                                        {isClientInvoiceEditable(invoice) && <Button data-action-tone="edit" variant="secondary" size="icon"  className="recordActionIcon" aria-label="Edit Invoice" title="Edit Invoice" onClick={() => openInvoiceEditor(invoice)}><EditIcon /></Button>}
                                                        <Button variant="secondary" size="icon" className="recordActionIcon" aria-label="Duplicate Invoice" title="Duplicate Invoice" onClick={() => setInvoicePendingDuplicate(invoice)}><CopyIcon /></Button>
                                                        {invoice.status !== 'Voided' && <Button variant="secondary" size="icon" className="recordActionIcon invoiceEmailActionIcon" aria-label={invoice.status === 'Draft' ? 'Send Invoice' : 'Resend Invoice'} title={invoice.status === 'Draft' ? 'Send Invoice' : 'Resend Invoice'} onClick={() => emailInvoice(invoice)}><EmailOutlinedIcon fontSize="small" /></Button>}
                                                        <Button variant="secondary" size="icon" type="button" className="recordActionIcon" aria-label="Preview Invoice" title="Preview Invoice" onClick={() => openInvoicePreview(invoice)}><EyeIcon /></Button>
                                                        <Button variant="secondary" size="icon" className="recordActionIcon" aria-label="Download Invoice" title="Download Invoice" onClick={() => { setShouldUploadAndSendInvoice(false); setChosenInvoice(invoice) }}><DownloadIcon /></Button>
                                                        {invoice.status === 'Voided' && <Button variant="secondary" size="icon" type="button" className="recordActionIcon" aria-label="Restore invoice to draft" title="Restore invoice to draft" onClick={() => handleMarkAsCreated(invoice.id)}><ResetIcon /></Button>}
                                                        {invoice.status !== 'Voided' && invoice.status !== 'Paid' && <Button data-action-tone="delete" variant="danger" size="icon"  className="recordActionIcon invoiceLineDeleteIcon" type="button" aria-label="Void Invoice" title="Void Invoice" onClick={() => { setInvoicePendingVoid(invoice); setVoidReason('') }}><TrashIcon /></Button>}
                                                        {!invoice.deliveryChangesPending && (invoice.status === 'Sent' || invoice.status === 'Outstanding' || invoice.status === 'Overdue') && (
                                                            <Button variant="secondary" size="icon" type="button" className="recordActionIcon invoicePaidActionIcon" aria-label="Record Payment" title="Record Payment" onClick={() => openMarkPaidModal(invoice)}><span className="invoicePaidDollar" aria-hidden="true">$</span></Button>
                                                        )}
                                                    </ActionMenu>
                                                </TableCell>
                                            </TableRow>
                                    })}
                                </tbody>
                            </Table>


                            {filteredClientInvoices.length > 0 && (
                                <div className='invoicePagination'>
                                    <Button variant="secondary" size="md" type='button' disabled={clientInvoicePage === 1} onClick={() => setClientInvoicePage(page => Math.max(1, page - 1))}>Previous</Button>
                                    <span>Page {clientInvoicePage} of {clientInvoicePageCount}</span>
                                    <Button variant="secondary" size="md" type='button' disabled={clientInvoicePage === clientInvoicePageCount} onClick={() => setClientInvoicePage(page => Math.min(clientInvoicePageCount, page + 1))}>Next</Button>
                                </div>
                            )}
                        </div>
                    </Section>
                </>
                    : activeTab === 'Workers' ? <>
                        <SectionCard title={<>Generate Worker Invoices</>} className='sectionCard width-100 invoiceManualGenerationCard' subtitle={<>Create one invoice per worker from completed timesheets in the selected period. Site-specific worker rates are applied where enabled.</>} bodyClassName="sectionBody sectionCardBody">

                                <div className='invoiceManualGenerationBlock'>

                                    <div className='invoiceManualGenerationGrid'>
                                        <div className='invoiceFilterItem'><MyCalendar disabled={false} showDateOnly showYearDropdown startDate={manualWorkerInvoiceStartDate} setStartDate={setManualWorkerInvoiceStartDate} label={'Start Date'} /></div>
                                        <div className='invoiceFilterItem'><MyCalendar disabled={false} showDateOnly showYearDropdown startDate={manualWorkerInvoiceEndDate} setStartDate={setManualWorkerInvoiceEndDate} label={'End Date'} /></div>
                                        <div className='invoiceFilterItem'>
                                            <Dropdown label={'Worker'} options={manualInvoiceWorkerOptions} value={manualInvoiceWorkerOptions.find(option => option.value === manualInvoiceWorkerId) || manualInvoiceWorkerOptions[0]} setValue={value => setManualInvoiceWorkerId(value?.value || '')} />
                                        </div>
                                    </div><div className='invoiceManualGenerationAction invoiceGenerationActions'><Button variant="create" size="md" className='invoiceInlineSaveButton saveWithIcon' onClick={previewManualWorkerInvoiceGeneration}>Generate Worker Invoices</Button></div>
                                    {invoiceGenerationMessage && <div className='invoiceGenerationPageMessage' role='status'>{invoiceGenerationMessage}</div>}
                                </div>

                        </SectionCard>
                        <SectionCard className='sectionCard width-100' bodyClassName="sectionBody sectionCardBody">

                                <div className="invoiceSummaryGrid invoiceSummaryGridFour">
                                    <StatCard icon={<ReceiptIcon />} label={<>Outstanding Worker Amount</>} value={<>{formatCurrency(workerInvoiceSummary.outstandingAmount)}</>} tone="blue" />
                                    <StatCard icon={<ReceiptIcon />} label={<>Overdue Worker Amount</>} value={<>{formatCurrency(workerInvoiceSummary.overdueAmount)}</>} tone="red" />
                                    <StatCard icon={<ReceiptIcon />} label={<>Overdue Worker Invoice</>} value={<>{workerInvoiceSummary.overdueCount}</>} tone="red" />
                                    <StatCard icon={<ReceiptIcon />} label={<>Paid Worker Amount</>} value={<>{formatCurrency(workerInvoiceSummary.paidAmount)}</>} tone="green" />
                                </div>

                        </SectionCard>
                        <Section title={'Worker Invoices'} className='width-100' variant='card'>
                            <div className="invoiceListShell">
                                <div className="invoiceFilterBar">
                                    <div className="invoiceFilterItem">
                                        <Dropdown
                                            options={workerStatusFilterOptions}
                                            value={selectedWorkerStatusFilterOption}
                                            setValue={value => setWorkerStatusFilter(value?.value || 'all')}
                                        />
                                    </div>
                                    <div className="invoiceFilterItem">
                                        <Dropdown
                                            options={dateFilterOptions}
                                            value={selectedWorkerDateFilterOption}
                                            setValue={value => setWorkerDateFilter(value?.value || 'all')}
                                        />
                                    </div>
                                    {workerDateFilter === 'custom' && (
                                        <>
                                            <div className="invoiceFilterItem">
                                                <MyCalendar disabled={false} showDateOnly showYearDropdown startDate={workerCustomDateFrom} setStartDate={setWorkerCustomDateFrom} label={'From'} />
                                            </div>
                                            <div className="invoiceFilterItem">
                                                <MyCalendar disabled={false} showDateOnly showYearDropdown startDate={workerCustomDateTo} setStartDate={setWorkerCustomDateTo} label={'To'} />
                                            </div>
                                        </>
                                    )}
                                    <div className="invoiceSearchItem">
                                        <SearchInput
                                            className="invoiceSearchInput"
                                            value={workerSearchTerm}
                                            onChange={(event) => setWorkerSearchTerm(event.target.value)}
                                            placeholder="Search by worker name"
                                        />
                                    </div>
                                </div>
                                <Table emptyItems="invoices" className="invoiceListTable">
                                    <TableHead>
                                        <TableRow>
                                            <TableHeaderCell>
                                                Invoice Number
                                            </TableHeaderCell>

                                            <TableHeaderCell>
                                                Date
                                            </TableHeaderCell>
                                            <TableHeaderCell>
                                                Worker
                                            </TableHeaderCell>
                                            <TableHeaderCell>
                                                Status
                                            </TableHeaderCell>
                                            <TableHeaderCell>
                                                Action
                                            </TableHeaderCell>
                                        </TableRow>
                                    </TableHead>
                                    <tbody>
                                        {paginatedWorkerInvoices.map(invoice => {
                                            const statusMeta = getInvoiceStatusMeta(invoice)
                                            const statusLabel = invoice.status === 'Sent' ? 'Received' : statusMeta.label
                                            return <TableRow flagged={invoice.status === 'Overdue'} key={invoice.id}>
                                                <TableCell data-label="Invoice Number" onClick={() => openInvoiceReview(invoice)} className="hover invoiceNumberCell">
                                                    <span className='allocationDateLink'>{invoice.invoiceNumber}</span>
                                                </TableCell>
                                                <TableCell data-label="Date">{format(new Date(invoice.date), 'EEE, d MMM yyyy')}</TableCell>
                                                <TableCell data-label="Worker">{formatName(invoice.workerName)}</TableCell>
                                                <TableCell data-label="Status"><span className={`invoiceStatusPill invoiceStatusPill${statusMeta.tone.charAt(0).toUpperCase() + statusMeta.tone.slice(1)}`}>{statusLabel}</span></TableCell>
                                                <TableCell data-label="Action">
                                                    <ActionMenu className="invoiceRowActions workerInvoiceRowActions">
                                                        {isWorkerInvoiceEditable(invoice) && <Button data-action-tone="edit" variant="secondary" size="icon"  type="button" className="recordActionIcon" aria-label="Edit Invoice" title="Edit Invoice" onClick={() => openInvoiceEditor(invoice)}><EditIcon /></Button>}
                                                        <Button variant="secondary" size="icon" type="button" className="recordActionIcon" aria-label="Duplicate Invoice" title="Duplicate Invoice" onClick={() => setInvoicePendingDuplicate(invoice)}><CopyIcon /></Button>
                                                        {(invoice.status === 'Sent' || invoice.status === 'Outstanding' || invoice.status === 'Overdue') && <Button variant="secondary" size="sm" type="button" className="textButton" aria-label="Revert and edit invoice" title="Revert and edit invoice" onClick={() => handleMarkAsCreated(invoice.id)}>Revert &amp; Edit</Button>}
                                                        <Button variant="secondary" size="icon" type="button" className="recordActionIcon" aria-label="Preview Invoice" title="Preview Invoice" onClick={() => openInvoicePreview(invoice)}><EyeIcon /></Button>
                                                        <Button variant="secondary" size="icon" type="button" className="recordActionIcon" aria-label="Download Invoice" title="Download Invoice" onClick={() => { setShouldUploadAndSendInvoice(false); setChosenInvoice(invoice) }}><DownloadIcon /></Button>
                                                        {(invoice.status === 'Sent' || invoice.status === 'Outstanding' || invoice.status === 'Overdue') && (
                                                            <Button variant="secondary" size="icon" type="button" className="recordActionIcon invoicePaidActionIcon" aria-label="Record Payment" title="Record Payment" onClick={() => openMarkPaidModal(invoice)}><span className="invoicePaidDollar" aria-hidden="true">$</span></Button>
                                                        )}
                                                    </ActionMenu>
                                                </TableCell>
                                            </TableRow>
                                        })}
                                    </tbody>
                                </Table>

                                {filteredWorkerInvoices.length > 0 && (
                                    <div className='invoicePagination'>
                                        <Button variant="secondary" size="md" type='button' disabled={workerInvoicePage === 1} onClick={() => setWorkerInvoicePage(page => Math.max(1, page - 1))}>Previous</Button>
                                        <span>Page {workerInvoicePage} of {workerInvoicePageCount}</span>
                                        <Button variant="secondary" size="md" type='button' disabled={workerInvoicePage === workerInvoicePageCount} onClick={() => setWorkerInvoicePage(page => Math.min(workerInvoicePageCount, page + 1))}>Next</Button>
                                    </div>
                                )}
                            </div>
                        </Section>
                    </> : activeTab === 'Quotes' ? <Quotes embedded /> : activeTab === 'Payroll' ? <PayrollLedger onSuccess={setSuccessMessage} orgId={activeOrg.id} setPageLoading={setLoading} setPageLoadingMessage={setLoadingMessage} /> : <SectionCard title={<>Client Statement</>} className='sectionCard width-100 invoiceStatementCard' subtitle={<>Generate a statement for a client’s paid invoices or their current outstanding and overdue invoices.</>} bodyClassName="sectionBody sectionCardBody">

                            <div className='invoiceManualGenerationBlock'>

                                <div className='invoiceStatementGrid'>
                                    <Dropdown
                                        label={'Client'}
                                        options={statementClientOptions}
                                        value={statementClientOptions.find(option => option.value === statementClientId) || statementClientOptions[0]}
                                        setValue={option => setStatementClientId(option?.value || '')}
                                    />
                                    <Dropdown
                                        label={'Statement Type'}
                                        options={statementTypeOptions}
                                        value={statementTypeOptions.find(option => option.value === statementType) || statementTypeOptions[0]}
                                        setValue={option => setStatementType(option?.value || 'outstanding')}
                                    />
                                    <Button variant="secondary" size="md" type='button' className='secondaryButton' disabled={statementBusy || !statementClientId} onClick={() => withDownloadProgress(() => generateClientStatement('download'))}>Download PDF</Button>
                                    <Button variant="secondary" size="md" type='button' className='invoiceInlineSaveButton' disabled={statementBusy || !statementClientId} onClick={() => generateClientStatement('email')}>{statementBusy ? 'Processing...' : 'Email Statement'}</Button>
                                </div>
                            </div>

                    </SectionCard>}

                <ModalComponent
                    showModal={showInvoiceFavourites}
                    setShowModal={setShowInvoiceFavourites}
                    customMaxWidth='720px'
                    customOverlayStyle={{ zIndex: 3100 }}
                >
                    <div className='invoiceFavouritesModal'>
                        <h2 className='modalTitle'>Previously Used Line Items</h2>
                        <p className='modalSubtitle'>Select a line item to add it to this invoice. Its saved description, rate and quantity settings will be copied.</p>
                        <div className='invoiceFavouriteList'>
                            {invoiceFavouriteRows.map((row, index) => <button type='button' key={`${row.description}-${index}`} onClick={() => addFavouriteLineItem(row)}>
                                <strong>{row.description}</strong>
                                <span>{row.textOnly ? 'Free text' : `${formatCurrency(row.rate)} · ${formatInvoiceQuantity(row.quantity, row.quantityType || 'Hours')}`}</span>
                            </button>)}
                            {!invoiceFavouriteRows.length && <p className='invoiceEmptyText'>No previously used manual line items were found.</p>}
                        </div>
                        <div className='modalFooter'><Button variant="secondary" size="md" className='secondaryButton' onClick={() => setShowInvoiceFavourites(false)}>Cancel</Button></div>
                    </div>
                </ModalComponent>

                <ModalComponent
                    showModal={!!invoicePreview}
                    setShowModal={(show) => { if (!show) closeInvoicePreview() }}
                    customMaxWidth='940px'
                    customContentStyle={{ width: 'min(940px, calc(100vw - 32px))', height: '88vh', padding: '14px' }}
                    customOverlayStyle={{ zIndex: 3200 }}
                >
                    <div className='invoicePdfPreviewModal'>
                        <div className='invoicePdfPreviewHeader'><div><h2 className='modalTitle'>Invoice Preview</h2><p>{invoicePreview?.invoiceNumber || ''}</p></div><Button variant="secondary" size="md" className='secondaryButton' onClick={closeInvoicePreview}>Cancel</Button></div>
                        {!invoicePreviewUrl && <div className='invoiceReviewLoading'>{invoicePreviewFailed ? "Couldn't generate the invoice preview. Close and try again." : 'Generating invoice preview...'}</div>}
                        {invoicePreviewUrl && <iframe title={`Invoice ${invoicePreview?.invoiceNumber || ''} preview`} src={invoicePreviewUrl} />}
                    </div>
                </ModalComponent>

                {previewInvoicePayload && <InvoiceGenerator
                    isWorker={!!invoicePreview.worker}
                    previewOnly
                    invoice={previewInvoicePayload}
                    logo={typeof activeOrgInfo?.logo === 'string' ? activeOrgInfo.logo : activeOrgInfo?.logo?.url}
                    onPreviewError={() => setInvoicePreviewFailed(true)}
                    onPreviewReady={(blob) => {
                        setInvoicePreviewUrl(current => {
                            if (current) URL.revokeObjectURL(current)
                            return URL.createObjectURL(blob)
                        })
                    }}
                />}

                {chosenInvoice && <InvoiceGenerator isWorker={activeTab === 'Workers'}
                    shouldUploadAndSendInvoice={shouldUploadAndSendInvoice}
                    logo={typeof activeOrgInfo?.logo === 'string' ? activeOrgInfo.logo : activeOrgInfo?.logo?.url}
                    setChosenInvoice={setChosenInvoice}
                    updateInvoice={handleSendInvoice}
                    setUploading={setUploading}
                    onDownloadComplete={() => downloadAllocationPdfsForInvoice(chosenInvoice)}
                    invoice={memoInvoice}
                // invoice={
                //     {
                //         company: {
                //             name: "Multicrew Pty Ltd",
                //             address: "Shop 1/40 Wollongong Road, Arncliffe NSW 2205",
                //             phone: "0434 826 821",
                //             email: "mahmoud@multicrew.com.au",
                //         },
                //         ...chosenInvoice
                //     }
                //     //     {
                //     //     // logoBase64: logo,
                //     //     invoiceNumber: "1538",
                //     //     date: "9 Oct, 2025",
                //     //     due: "30 Nov, 2025",
                //     //     company: {
                //     //         name: "Multicrew Pty Ltd",
                //     //         address: "Shop 1/40 Wollongong Road, Arncliffe NSW 2205",
                //     //         phone: "0434 826 821",
                //     //         email: "mahmoud@multicrew.com.au",
                //     //     },
                //     //     client: {
                //     //         name: "LORDS GROUP COMMERCIAL NSW PTY LTD",
                //     //         address: "(LG2130) 88-98 Ebley St, Bondi Junction",
                //     //     },
                //     //     jobs: [
                //     //         {
                //     //             description: "Traffic Controller x 2 PEDRO/FABIO (9/10 1600-1800)",
                //     //             quantity: 2,
                //     //             rate: 90.0,
                //     //             gst: 18.0,
                //     //             total: 198.0,
                //     //         },
                //     //         {
                //     //             description: "Traffic Controller x 2 (PM) PEDRO/FABIO (9/10 1800-0100)",
                //     //             quantity: 7,
                //     //             rate: 105.0,
                //     //             gst: 73.5,
                //     //             total: 808.5,
                //     //         },
                //     //         {
                //     //             description: "Traffic Controller x 1 (PM) GIOVANNA (9/10 1830-0100)",
                //     //             quantity: 6.5,
                //     //             rate: 52.5,
                //     //             gst: 34.13,
                //     //             total: 375.38,
                //     //         },
                //     //         {
                //     //             description: "Traffic Controller x 3 GIO/PEDRO/FABIO (10/10 1600-1800)",
                //     //             quantity: 2,
                //     //             rate: 132.75,
                //     //             gst: 26.55,
                //     //             total: 292.05,
                //     //         },
                //     //         {
                //     //             description: "Traffic Controller x 3 GIO/PEDRO/FABIO (10/10 1800-0100)",
                //     //             quantity: 7,
                //     //             rate: 157.5,
                //     //             gst: 110.25,
                //     //             total: 1212.75,
                //     //         },
                //     //     ],
                //     //     subtotal: 2624.25,
                //     //     gst: 262.43,
                //     //     total: 2886.68,
                //     // }
                // }
                />}

                {reviewInvoice && (
                    <>
                        <div className="invoiceReviewBackdrop" onMouseDown={closeInvoiceReview}>
                            <aside className="invoiceReviewPanel" role="dialog" aria-modal="true" aria-label={`Review invoice ${reviewInvoice.invoiceNumber || ''}`} onMouseDown={event => event.stopPropagation()}>
                                <header className="invoiceReviewHeader">
                                    <div>
                                        <span className="invoiceReviewEyebrow">Invoice review</span>
                                        <h2>{reviewInvoice.invoiceNumber || 'Invoice'}</h2>
                                        <p>{reviewInvoice.client?.name || reviewInvoice.clientName || formatName(reviewInvoice.workerName) || reviewInvoice.worker?.name || ''}</p>
                                    </div>
                                    <div className="invoiceReviewHeaderActions">
                                        <Button variant="secondary" size="md" type="button" className="invoiceReviewClose" onClick={closeInvoiceReview} aria-label="Close invoice review">Cancel</Button>
                                        <div className="invoiceReviewSecondaryActions">
                                            {reviewInvoice.client && (reviewInvoice.status || 'Draft') === 'Draft' && <Button variant="soft" size="md" type="button" disabled={loading || uploading} title="Save changes and review email" onClick={() => handleSaveAdditionalJobs({ reviewEmail: true })}>Send Email</Button>}
                                            {!reviewInvoice.deliveryChangesPending && ['Sent', 'Outstanding', 'Overdue'].includes(reviewInvoice.status) && <Button variant="secondary" size="md" type="button" className="secondaryButton invoiceReviewPaymentButton" disabled={loading} onClick={() => openMarkPaidModal(reviewInvoice)}>Record Payment</Button>}
                                            <Button variant="secondary" size="md" type="button" className="secondaryButton invoiceReviewDuplicateButton" disabled={loading} onClick={() => setInvoicePendingDuplicate(reviewInvoice)}>Duplicate</Button>
                                            {reviewInvoice.status === 'Voided' && <Button variant="secondary" size="icon" type="button" className="secondaryButton" aria-label="Restore invoice to draft" title="Restore invoice to draft" disabled={loading} onClick={() => handleMarkAsCreated(reviewInvoice.id)}><ResetIcon /></Button>}
                                        </div>
                                    </div>
                                </header>

                                <div className="invoiceReviewWorkspace">
                                    <section className="invoiceReviewDocument">
                                        <div className="invoiceReviewDocumentHeader">
                                            <div>
                                                <span>Invoice details</span>
                                                <strong>{reviewInvoice.invoiceNumber || ''}</strong>
                                            </div>
                                            {isInvoiceEditable(reviewInvoice) && (
                                                <Button data-action-tone="save" variant="soft" size="md"  type="button" className="invoiceReviewSaveButton" disabled={loading || uploading} onClick={handleSaveAdditionalJobs}>Save</Button>
                                            )}
                                        </div>
                                        <div className="invoiceReviewEditContent">
                                            <div className="invoiceEditMetaRow invoiceReviewMetaRow">
                                                <div className="invoiceEditMetaField"><Input disabled value={reviewInvoice.invoiceNumber || ''} setValue={() => { }} label="Invoice Number" /></div>
                                                <div className="invoiceEditMetaField"><Input disabled value={reviewInvoice.client?.name || reviewInvoice.clientName || reviewInvoice.workerName || reviewInvoice.worker?.name || ''} setValue={() => { }} label={reviewInvoice.client ? 'Client Name' : 'Worker'} /></div>
                                                {reviewInvoice.client && <div className="invoiceEditMetaField"><Input disabled value={reviewInvoice.site?.name || reviewInvoice.siteName || ''} setValue={() => { }} label="Site" /></div>}
                                                {reviewInvoice.client && <div className="invoiceEditMetaField"><Input disabled={!isClientInvoiceEditable(reviewInvoice)} value={purchaseOrderNumber} setValue={setPurchaseOrderNumber} label="PO Number" placeholder="Enter PO number" /></div>}
                                                {reviewInvoice.client && <div className="invoiceEditMetaField"><MyCalendar disabled={!isClientInvoiceEditable(reviewInvoice)} showDateOnly showYearDropdown noMaxDate startDate={invoiceDate} setStartDate={handleInvoiceDateChange} label="Invoice Date" /></div>}
                                                {reviewInvoice.client && <div className="invoiceEditMetaField"><Dropdown label="Due Terms" disabled={!isClientInvoiceEditable(reviewInvoice)} options={dueTermOptions} value={dueTermOptions.find(option => option.value === clientDueTerm) || dueTermOptions[1]} setValue={value => handleClientDueTermChange(value?.value)} /></div>}
                                                {reviewInvoice.client && <div className="invoiceEditMetaField"><MyCalendar disabled={!isClientInvoiceEditable(reviewInvoice)} showDateOnly showYearDropdown startDate={clientDueDate} setStartDate={setClientDueDate} label="Due Date" /></div>}
                                                {reviewInvoice.client && <FieldLabel choice className="invoiceEditReminderToggle"><UiCheckbox type="checkbox" disabled={!isClientInvoiceEditable(reviewInvoice)} checked={!!autoReminderEnabled} onChange={event => setAutoReminderEnabled(event.target.checked)} />Auto reminder for payment</FieldLabel>}
                                            </div>

                                            <div className="invoiceReviewTableScroll">
                                                <Table emptyItems="line items" className="invoiceLineItemsTable">
                                                    <TableHead><TableRow><TableHeaderCell>Date/Time</TableHeaderCell><TableHeaderCell>Description</TableHeaderCell><TableHeaderCell>Rate</TableHeaderCell><TableHeaderCell>Quantity</TableHeaderCell><TableHeaderCell>GST</TableHeaderCell><TableHeaderCell>Total</TableHeaderCell>{isInvoiceEditable(reviewInvoice) && <TableHeaderCell>Action</TableHeaderCell>}</TableRow></TableHead>
                                                    <tbody>
                                                        {[...(invoiceJobs || []).map((row, index) => ({ ...row, rowType: 'job', rowIndex: index })), ...(manualLineItems || []).map((row, index) => ({ ...row, rowType: 'manual', rowIndex: index }))].map((row, index) => {
                                                            const lineDateTime = getInvoiceLineDateTime(row.time)
                                                            const lineDescription = getInvoiceLineDescription(row)
                                                            return <TableRow key={`review-row-${row.rowType}-${index}`}>
                                                                <TableCell><div className="invoiceLineDate">{lineDateTime.date}</div>{lineDateTime.time && <div className="invoiceLineTime">{lineDateTime.time}</div>}</TableCell>
                                                                <TableCell><div className="invoiceLineRole">{lineDescription.role}</div>{lineDescription.names.map((name, nameIndex) => <div className="invoiceLineWorker" key={`${name}-${nameIndex}`}>{name}</div>)}</TableCell>
                                                                <TableCell>{row.textOnly ? '-' : formatCurrency(row.rate)}</TableCell>
                                                                <TableCell>{row.textOnly ? '-' : formatInvoiceQuantity(row.quantity, getInvoiceQuantityType(row))}</TableCell>
                                                                <TableCell>{row.textOnly ? '-' : formatCurrency(row.gst ?? calculateAdditionalJobRowGst(row))}</TableCell>
                                                                <TableCell>{row.textOnly ? '-' : formatCurrency(row.total ?? (calculateAdditionalJobRowSubTotal(row) + calculateAdditionalJobRowGst(row)))}</TableCell>
                                                                {isInvoiceEditable(reviewInvoice) && <TableCell><div className="invoiceLineItemActions"><Button data-action-tone="edit" title="Edit line item" variant="secondary" size="icon"  className="recordActionIcon invoiceLineEditIcon" aria-label="Edit line item" onClick={() => handleEditJob(row, row.rowIndex, row.rowType === 'job')}><EditIcon /></Button><Button data-action-tone="delete" title="Delete line item" variant="danger" size="icon"  className="recordActionIcon invoiceLineDeleteIcon" aria-label="Delete line item" onClick={() => setLineItemPendingDelete(row)}><TrashIcon /></Button></div></TableCell>}
                                                            </TableRow>
                                                        })}
                                                        {isInvoiceEditable(reviewInvoice) && <TableRow className="invoiceDiscountRow">
                                                            <TableCell></TableCell>
                                                            <TableCell><UiInput className="invoiceDiscountDescription" value={discountDescription} onChange={event => setDiscountDescription(event.target.value)} aria-label="Discount description" /></TableCell>
                                                            <TableCell><div className="invoiceDiscountAmountField"><span>$</span><UiInput inputMode="decimal" value={discountAmount} onChange={event => setDiscountAmount(event.target.value.replace(/[^0-9.]/g, ''))} placeholder="0.00" aria-label="Discount amount" /></div></TableCell>
                                                            <TableCell>1 Unit</TableCell>
                                                            <TableCell>-{formatCurrency(invoiceEditTotals.discountGst)}</TableCell>
                                                            <TableCell>-{formatCurrency(invoiceEditTotals.discountAmount + invoiceEditTotals.discountGst)}</TableCell>
                                                            <TableCell><FieldLabel choice className="invoiceDiscountGstToggle"><UiCheckbox type="checkbox" checked={discountGstEnabled} onChange={event => setDiscountGstEnabled(event.target.checked)} /> GST</FieldLabel></TableCell>
                                                        </TableRow>}
                                                        {isInvoiceEditable(reviewInvoice) && <TableRow className="invoiceNewLineItemRow"><TableCell colSpan={7}><div className="invoiceLineItemAddActions"><Button variant="create" size="md" className="invoiceInlineLink" onClick={() => { setShowAdditionalJobIndex(manualLineItems.length); setShowEditingPrimaryJob(false); clearJobRateFields(); setShowEditAdditionalJob(true) }}>+ New Line Item</Button><Button variant="create" size="md" className="invoiceInlineLink" onClick={() => { setShowAdditionalJobIndex(manualLineItems.length); setShowEditingPrimaryJob(false); clearJobRateFields(); setAdditionalJobTextOnly(true); setShowEditAdditionalJob(true) }}>+ Free Text</Button><Button variant="create" size="md" className="invoiceInlineLink" onClick={() => setShowInvoiceFavourites(true)}>+ Favourites</Button></div></TableCell></TableRow>}
                                                    </tbody>
                                                </Table>
                                            </div>

                                            {(reviewInvoice.client || reviewInvoice.worker) && (
                                                <div className="invoiceReviewTotals">
                                                    <span>Subtotal</span><strong>{formatCurrency(invoiceEditTotals.subtotal)}</strong>
                                                    <span>GST</span><strong>{formatCurrency(invoiceEditTotals.gst)}</strong>
                                                    <span>Total</span><strong>{formatCurrency(invoiceEditTotals.total)}</strong>
                                                    {getInvoicePaidAmount(reviewInvoice) > 0 && <><span>Paid</span><strong>{formatCurrency(getInvoicePaidAmount(reviewInvoice))}</strong><span>Balance</span><strong>{formatCurrency(getInvoiceBalanceDue(reviewInvoice))}</strong></>}
                                                </div>
                                            )}

                                            <InvoicePaymentHistory invoice={reviewInvoice} />

                                            <div className="invoiceHistoryBlock invoiceEditDividerSection">
                                                <h2 className="invoiceEditSectionHeading">History</h2>
                                                <Table emptyItems="activity entries"><TableHead><TableRow><TableHeaderCell>Date/Time</TableHeaderCell><TableHeaderCell>Action</TableHeaderCell><TableHeaderCell>Status</TableHeaderCell><TableHeaderCell>By</TableHeaderCell></TableRow></TableHead><tbody>
                                                    {(reviewInvoice.history || []).slice().reverse().map((row, index) => <TableRow key={`review-history-${index}`}><TableCell>{row.date ? <>{format(new Date(row.date), 'EEE, d MMM yyyy')}<br />{formatTime(new Date(row.date))}</> : '-'}</TableCell><TableCell>{row.action || row.type || '-'}{row.details && <div className="invoiceHistoryDetails">{row.details}</div>}</TableCell><TableCell>{row.status || '-'}</TableCell><TableCell>{formatName(row.actorName) || '-'}</TableCell></TableRow>)}
                                                </tbody></Table>
                                            </div>
                                        </div>
                                    </section>

                                    <section className="invoiceReviewDocument">
                                        <div className="invoiceReviewDocumentHeader invoiceReviewTimesheetHeader">
                                            <div>
                                                <span>Allocation timesheets</span>
                                                <strong>{reviewTimesheets.length ? `${reviewTimesheetIndex + 1} of ${reviewTimesheets.length}` : 'No timesheets'}</strong>
                                            </div>
                                            {reviewTimesheets.length > 0 && (
                                                <div className="invoiceReviewNavigation">
                                                    <Button variant="secondary" size="md" type="button" onClick={() => changeReviewTimesheet(-1)} aria-label="Previous timesheet">Previous</Button>
                                                    <Button variant="secondary" size="md" type="button" onClick={() => changeReviewTimesheet(1)} aria-label="Next timesheet">Next</Button>
                                                </div>
                                            )}
                                        </div>
                                        <div className="invoiceReviewFileName">
                                            {reviewTimesheets[reviewTimesheetIndex]?.name || ''}
                                            {reviewTimesheets[reviewTimesheetIndex]?.url && <a href={reviewTimesheets[reviewTimesheetIndex].url} target="_blank" rel="noreferrer">Open PDF</a>}
                                        </div>
                                        <div className="invoiceReviewPdfFrame">
                                            {reviewTimesheetsLoading && <div className="invoiceReviewLoading">Loading linked timesheets...</div>}
                                            {!reviewTimesheetsLoading && reviewTimesheetsError && <div className="invoiceReviewError">{reviewTimesheetsError}</div>}
                                            {!reviewTimesheetsLoading && !reviewTimesheetsError && reviewTimesheets.length === 0 && <div className="invoiceReviewEmpty">No allocation timesheets are linked to this invoice.</div>}
                                            {!reviewTimesheetsLoading && reviewTimesheets[reviewTimesheetIndex]?.url && (
                                                <iframe
                                                    key={reviewTimesheets[reviewTimesheetIndex].url}
                                                    title={`Timesheet ${reviewTimesheetIndex + 1} of ${reviewTimesheets.length}`}
                                                    src={reviewTimesheets[reviewTimesheetIndex].url}
                                                />
                                            )}
                                        </div>
                                    </section>
                                </div>
                            </aside>
                        </div>
                    </>
                )}

            </PageContent>
        </div>
    </div >
}

export default Invoices
